import {
  ColorPalette, PageSection, ReportDesign, ReportStatistics, ReportSubSection, ReportType, ReportTypeDataMap,
} from '../../models/end-client-reporting-package';
import {
  Correction, Direction, MeanStats, columnLetter, effectiveBase, meanStats, meanZ, proportionZ,
  resolveStatistics, significantTests, twoTailedP,
} from './crosstab-stats';
// Type-only, and it has to stay that way: report-design-edit -> chart-layout -> report-aggregation
// is a real cycle at runtime, which the esbuild builder rejects. `import type` is erased entirely.
import type { BlockPatch } from './report-design-edit';
import type { ExploreHandle } from './crosstab-explore';

export type AnswerValue = string | number;

export interface AnswerOption {
  value: AnswerValue;
  label: string;
}

export type SurveyVariable =
  // `partOf` is declared on every member so it can be read without narrowing first; a coded
  // variable is a question in its own right and never carries one.
  //
  // `group` is separate and display-only: the question a variable is *listed under*, which a grid's
  // rows need and a battery's columns also have. It must not be conflated with `partOf` -- a grid
  // row is a question in its own right and a perfectly good filter, where a battery column is not.
  | { kind: 'coded'; label: string; column: string; options: AnswerOption[]; partOf?: undefined; group?: string }
  | { kind: 'multi'; label: string; columns: { column: string; label: string }[]; partOf?: string; group?: string }
  // A battery of numeric columns -- MaxDiff scores, a constant-sum allocation -- holding a number
  // per respondent rather than a coded answer. Shaped like 'multi' on purpose: one column per item,
  // so everything that walks columns keeps working. What differs is the aggregation, which is a
  // mean rather than a count (see aggregate()).
  | { kind: 'numeric'; label: string; columns: { column: string; label: string }[]; partOf?: string; group?: string }
  // An open-end: one column holding what the respondent typed. Shaped like 'coded' -- a single
  // column, no options -- because there is nothing to enumerate. Nothing counts it: every function
  // here that turns answers into figures returns empty for it, and the only module that reads one
  // shows the text itself. Kept in the layout all the same so an open-end can be referenced,
  // labelled and resolved like any other question.
  | { kind: 'text'; label: string; column: string; partOf?: undefined; group?: string }
  // An image-highlighter question: respondents drag boxes over an image, and each click category
  // ("like", "dislike") is a text column holding that respondent's boxes as JSON. Nothing here counts
  // it -- only the heatmap module reads it, off `heatmapSeries` below.
  | {
    kind: 'heatmap';
    label: string;
    columns: { column: string; label: string }[];
    /** The "none of these" answer, a 0/1 column of its own, where the question has one. */
    optOut?: { column: string; label: string };
    partOf?: undefined;
    group?: string;
  };

export type SurveyLayout = Record<string, SurveyVariable>;

export type RespondentRecord = Record<string, AnswerValue | AnswerValue[]>;

export type ActiveFilters = Record<string, string>;

export interface VariableAggregate {
  points: ChartPoint[];
  base: number;
  multiResponse: boolean;
  known: boolean;
  /**
   * Each point is a mean rather than a share of the base. Carried through to ChartSeries because a
   * block has to know before it prints a "%" on anything.
   */
  numeric?: boolean;
  /** How many respondents answered, unweighted. Equal to `base` when the deck is not weighted. */
  sampleBase?: number;
  /** The answering respondents' weights summed, unrounded -- what every percentage is taken over. */
  sumWeights?: number;
  /** Their weights squared and summed, which with `sumWeights` gives the effective base. */
  sumSquares?: number;
}

/**
 * A respondent's weight. Absent means unweighted, and every function taking one behaves exactly as
 * it did before weighting existed.
 *
 * Weighted counts are rounded to whole respondents in `value` and `base`, the figures every module
 * prints as a count or an `n=`; percentages are taken over the unrounded sums, so rounding never
 * moves a share. With weights averaging 1 the rounded figures read as head counts, which is what
 * the modules that print them were written to show.
 */
export type WeightOf = (record: RespondentRecord) => number;

export type AggregateTable = Record<string, VariableAggregate>;

const EMPTY_AGGREGATE: VariableAggregate = { points: [], base: 0, multiResponse: false, known: false };

function assertNever(value: never): never {
  throw new Error(`Unhandled report type: ${JSON.stringify(value)}`);
}

function isSelected(value: AnswerValue | AnswerValue[] | undefined): boolean {
  if (value === undefined || value === null || Array.isArray(value)) return false;
  const text = String(value).trim();
  return text !== '' && text !== '0';
}

function codesOf(record: RespondentRecord, column: string): string[] {
  const value = record[column];
  if (value === undefined || value === null) return [];
  return (Array.isArray(value) ? value : [value])
    .map(v => String(v).trim())
    .filter(code => code !== '');
}

// Decipher writes 0 for an option a respondent saw and did not pick, and omits the columns
// entirely when they never saw the question, so present is not the same as selected.
function isPresent(value: AnswerValue | AnswerValue[] | undefined): boolean {
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) return value.length > 0;
  return String(value).trim() !== '';
}

function variableOf(
  key: string,
  variableMap: Record<string, string>,
  layout: SurveyLayout,
): SurveyVariable | null {
  const position = variableMap[key];
  return position ? layout[position] ?? null : null;
}

/**
 * The design's own names for variables whose data position isn't in the loaded datamap. Charts using
 * them render "No data for …" per block; this is the same fact stated once for the whole deck.
 *
 * Computed at render from the design and datamap in front of it, so it can't go stale the way a
 * stored flag would — and it holds whatever put the deck in that state: a source switch, a
 * structure refresh that dropped a question, or a design generated against something else.
 */
export function unresolvedVariables(design: ReportDesign, layout: SurveyLayout): string[] {
  const variableMap = design?.variableMap ?? {};
  return Object.keys(variableMap).filter(key => !variableOf(key, variableMap, layout));
}

export function applyFilters(
  records: RespondentRecord[],
  filters: ActiveFilters,
  variableMap: Record<string, string>,
  layout: SurveyLayout,
): RespondentRecord[] {
  const active = Object.entries(filters)
    .filter(([, value]) => !!value)
    .map(([key, value]) => ({ variable: variableOf(key, variableMap, layout), value }))
    .filter((f): f is { variable: SurveyVariable; value: string } => !!f.variable);

  if (!active.length) return records;
  return records.filter(record => active.every(f => f.variable.kind === 'coded'
    ? codesOf(record, f.variable.column).includes(f.value)
    : isSelected(record[f.value])));
}

export function optionsFor(
  key: string,
  variableMap: Record<string, string>,
  layout: SurveyLayout,
): AnswerOption[] {
  const variable = variableOf(key, variableMap, layout);
  if (!variable) return [];
  // An open-end has no answer list: everyone wrote something different, which is the point of it.
  // Nor does a heatmap, whose answers are boxes on an image.
  if (variable.kind === 'text' || variable.kind === 'heatmap') return [];
  return variable.kind === 'coded'
    ? variable.options
    : variable.columns.map(column => ({ value: column.column, label: column.label }));
}

/** What the respondent typed, trimmed, or empty where they typed nothing. */
export function textOf(value: AnswerValue | AnswerValue[] | undefined): string {
  if (value === null || value === undefined || Array.isArray(value)) return '';
  return String(value).trim();
}

/** A numeric cell, or null where the respondent has no number there. */
function numberOf(value: AnswerValue | AnswerValue[] | undefined): number | null {
  if (!isPresent(value) || Array.isArray(value)) return null;
  const parsed = Number.parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

export function filterOptions(
  records: RespondentRecord[],
  key: string,
  variableMap: Record<string, string>,
  layout: SurveyLayout,
): AnswerOption[] {
  const variable = variableOf(key, variableMap, layout);
  if (!variable) return [];

  // A score has no options to filter or group by -- every respondent has their own number, so a
  // "filter to this value" would select one person. Banding it is a different feature.
  if (variable.kind === 'numeric') return [];
  // Nor does an open-end, for the same reason and more so: a dropdown of 460 paragraphs. Nor a
  // heatmap, whose answers are coordinates.
  if (variable.kind === 'text' || variable.kind === 'heatmap') return [];
  // Nor does one column of a question: "filter to people who picked this" is a real thing to want,
  // but it is a different control from "choose an answer", and a one-entry dropdown reads as broken.
  if (variable.partOf) return [];

  if (variable.kind === 'multi') {
    return variable.columns
      .filter(column => records.some(record => isSelected(record[column.column])))
      .map(column => ({ value: column.column, label: column.label }));
  }

  const present = new Set<string>();
  records.forEach(record => codesOf(record, variable.column).forEach(code => present.add(code)));
  return variable.options.filter(option => present.has(String(option.value)));
}

/** The respondents who answered a question, which is who its percentages are taken over. */
function answering(records: RespondentRecord[], variable: SurveyVariable): RespondentRecord[] {
  // Whoever typed something. A blank or whitespace-only cell is a respondent who skipped it, and
  // counting those would put a base on the module that its own table cannot show rows for.
  if (variable.kind === 'text') {
    return records.filter(record => textOf(record[variable.column]));
  }
  // Everyone who answered, including those who chose "none of these" -- so a share of the base is a
  // share of everyone asked, the way a single-select question is percentaged. Decipher writes 0 to
  // the opt-out column for anyone who drew instead, so where there is one, present means answered.
  if (variable.kind === 'heatmap') {
    const optOut = variable.optOut;
    return records.filter(record =>
      (optOut && isPresent(record[optOut.column]))
      || variable.columns.some(column => textOf(record[column.column])));
  }
  if (variable.kind === 'numeric') {
    // isPresent, not isSelected: 0 is a real score, and the lowest one a MaxDiff item can get.
    return records.filter(record =>
      variable.columns.some(column => isPresent(record[column.column])));
  }
  if (variable.kind === 'multi') {
    return records.filter(record =>
      variable.columns.some(column => isPresent(record[column.column])));
  }
  return records.filter(record => codesOf(record, variable.column).length > 0);
}

export function baseFor(records: RespondentRecord[], variable: SurveyVariable): number {
  return answering(records, variable).length;
}

/**
 * Everyone in `records`, weighted and unrounded -- a subgroup's size as compare mode rebases onto it.
 * Unrounded because it is arithmetic, not a figure anyone reads.
 */
export function weightedSize(records: RespondentRecord[], weightOf?: WeightOf): number {
  if (!weightOf) return records.length;
  return records.reduce((sum, record) => sum + weightOf(record), 0);
}

export function aggregate(
  records: RespondentRecord[],
  key: string,
  variableMap: Record<string, string>,
  layout: SurveyLayout,
  weightOf?: WeightOf,
): VariableAggregate {
  const variable = variableOf(key, variableMap, layout);
  if (!variable) return EMPTY_AGGREGATE;

  const weigh = weightOf ?? (() => 1);
  const answered = answering(records, variable);
  const sampleBase = answered.length;
  let sumWeights = 0;
  let sumSquares = 0;
  for (const record of answered) {
    const weight = weigh(record);
    sumWeights += weight;
    sumSquares += weight * weight;
  }
  const base = weightOf ? Math.round(sumWeights) : sampleBase;
  const sizes = { base, sampleBase, sumWeights, sumSquares };

  // Open-ends are never counted. `known` so a block naming one does not read as a broken reference,
  // and no points at all so nothing can turn free text into a percentage: the only module that
  // reads one takes the answers themselves, off `verbatimSeries` below. A heatmap likewise, off
  // `heatmapSeries`.
  if (variable.kind === 'text' || variable.kind === 'heatmap') {
    return { points: [], ...sizes, multiResponse: false, known: true };
  }
  // Shares off the unrounded weighted sums; only the printed count is rounded.
  const withShare = (counted: { label: string; value: number; optionIndex: number }[]): ChartPoint[] =>
    counted.map(point => ({
      ...point,
      value: Math.round(point.value),
      percent: sumWeights > 0 ? (point.value / sumWeights) * 100 : 0,
    }));

  if (variable.kind === 'numeric') {
    // The mean of each item across everyone who answered it. `value` carries the mean as well as
    // `percent` so that sorting -- which ranks on `value` -- ranks by score without a special case;
    // there is no count here for it to hold instead.
    return {
      points: variable.columns.map((column, optionIndex) => {
        let total = 0;
        let weights = 0;
        for (const record of records) {
          const n = numberOf(record[column.column]);
          if (n === null) continue;
          const weight = weigh(record);
          total += n * weight;
          weights += weight;
        }
        const mean = weights > 0 ? total / weights : 0;
        return { label: column.label, value: mean, percent: mean, optionIndex };
      }),
      ...sizes,
      multiResponse: false,
      known: true,
      numeric: true,
    };
  }

  if (variable.kind === 'multi') {
    return {
      points: withShare(variable.columns.map((column, optionIndex) => ({
        label: column.label,
        value: records.reduce(
          (sum, record) => isSelected(record[column.column]) ? sum + weigh(record) : sum, 0),
        optionIndex,
      }))),
      ...sizes,
      multiResponse: true,
      known: true,
    };
  }

  const counts = new Map<string, number>();
  records.forEach(record => {
    const weight = weigh(record);
    codesOf(record, variable.column).forEach(code => counts.set(code, (counts.get(code) ?? 0) + weight));
  });

  const points = variable.options.flatMap((option, optionIndex) => {
    const value = counts.get(String(option.value));
    return value === undefined ? [] : [{ label: option.label, value, optionIndex }];
  });

  const known = new Set(variable.options.map(option => String(option.value)));
  const unknown = [...counts.entries()]
    .filter(([code]) => !known.has(code))
    .map(([code, count], i) => ({ label: code, value: count, optionIndex: variable.options.length + i }));

  const counted = [...points, ...unknown];
  const total = counted.reduce((sum, point) => sum + point.value, 0);

  return {
    points: withShare(counted),
    ...sizes,
    // A tolerance rather than a bare `>`: summed weights carry float error, and one respondent's
    // rounding must not mark a single-select question as multi-punch.
    multiResponse: total > sumWeights + 1e-6,
    known: true,
  };
}

export function aggregateAll(
  records: RespondentRecord[],
  keys: string[],
  variableMap: Record<string, string>,
  layout: SurveyLayout,
  weightOf?: WeightOf,
): AggregateTable {
  const table: AggregateTable = {};
  keys.forEach(key => { table[key] = aggregate(records, key, variableMap, layout, weightOf); });
  return table;
}

/**
 * Decipher's own total interview time, in seconds. Present in every export -- the ingestion pulls
 * with no `fields=` narrowing -- unless a survey's "navigator" layout happens to exclude it, which
 * is why every consumer has to cope with it being missing.
 */
const LOI_SECONDS_COLUMN = 'qtime';

/** How the study was run, as opposed to what anyone answered. Read by the methodology module. */
export interface StudyFacts {
  sampleSize: number;
  /** Null when no record carries a usable qtime, so the module can say so rather than show NaN. */
  medianLoiMinutes: number | null;
  /**
   * Percent, or null when the counts are unknown -- which is the normal case in demo mode, and for
   * any project ingested before the counts were recorded. Never guessed from the records: they are
   * qualified-only, so deriving it there would report 100% every time.
   */
  incidenceRatePercent: number | null;
}

/** The screen-out counts an ingestion recorded. Both optional: an older run has neither. */
export interface RespondentCounts {
  completesCount?: number;
  terminatesCount?: number;
}

/**
 * Computed over every respondent, never a filtered subset: a methodology slide describes the study
 * as fielded, so it has to read the same whoever the deck is currently filtered to.
 */
export function studyFacts(records: RespondentRecord[], counts?: RespondentCounts): StudyFacts {
  const minutes = records
    .map(record => Number(record[LOI_SECONDS_COLUMN]))
    .filter(seconds => Number.isFinite(seconds) && seconds > 0)
    .map(seconds => seconds / 60)
    .sort((a, b) => a - b);

  const middle = Math.floor(minutes.length / 2);
  const median = !minutes.length
    ? null
    : minutes.length % 2
      ? minutes[middle]
      : (minutes[middle - 1] + minutes[middle]) / 2;

  // completes / (completes + terminates). Overquota is excluded upstream: those respondents
  // qualified and were turned away by a full quota, so counting them would understate incidence.
  const completes = counts?.completesCount;
  const terminates = counts?.terminatesCount;
  const screened = completes === undefined || terminates === undefined ? null : completes + terminates;

  return {
    // The recorded count is the authority when there is one: it is what Decipher reported for the
    // pull, whereas records.length counts whatever ended up in the file. They agree for a healthy
    // ingest -- data.json is that same qualified pull -- so this only differs when something went
    // wrong, or in demo mode, where there is no ingestion row to record anything.
    sampleSize: counts?.completesCount ?? records.length,
    medianLoiMinutes: median,
    incidenceRatePercent: screened && completes !== undefined ? (completes / screened) * 100 : null,
  };
}

export function variableLabel(
  key: string,
  variableMap?: Record<string, string>,
  layout?: SurveyLayout,
): string {
  if (!variableMap || !layout) return key;
  return variableOf(key, variableMap, layout)?.label ?? key;
}

export interface PickableVariable {
  key: string;          // the design's variableMap key, or the layout position when unmapped
  position: string;     // the data position the key resolves to
  label: string;        // question text from the datamap
  kind: SurveyVariable['kind'];
  /** Answer options for a coded variable; items for a multi-punch or numeric battery. */
  optionCount: number;
  /** The question this is one column of, when it is one. Not pickable in its own right. */
  partOf?: string;
  /** The question this is listed under, for a grid's rows and a battery's items. */
  group?: string;
  mapped: boolean;      // already in variableMap, so a block can reference it as-is
}

/**
 * Datamap positions in the order a person reads a survey: Q2 before Q10, and Q34r2 before Q34r10.
 * A plain string sort files Q10 and Q11 between Q1 and Q2, which is what makes a question list look
 * shuffled however carefully it was built.
 */
export function byDataPosition(a: string, b: string): number {
  const parts = (text: string) => text.match(/\d+|\D+/g) ?? [];
  const left = parts(a);
  const right = parts(b);

  for (let i = 0; i < Math.max(left.length, right.length); i++) {
    const x = left[i];
    const y = right[i];
    // The shorter one first, so "Q34" sorts above "Q34r1" rather than among its own items.
    if (x === undefined) return -1;
    if (y === undefined) return 1;

    const numeric = /^\d/.test(x) && /^\d/.test(y);
    const order = numeric ? Number(x) - Number(y) : x.localeCompare(y);
    if (order) return order;
  }

  return 0;
}

/**
 * Answers for a coded question, items for a battery, click categories for a heatmap, and nothing at
 * all for an open-end.
 */
function optionCountOf(variable: SurveyVariable): number {
  if (variable.kind === 'coded') return variable.options.length;
  if (variable.kind === 'text') return 0;
  return variable.columns.length;
}

export function listVariables(design: ReportDesign, layout: SurveyLayout): PickableVariable[] {
  const describe = (key: string, position: string, variable: SurveyVariable, mapped: boolean): PickableVariable => ({
    key,
    position,
    label: variable.label,
    kind: variable.kind,
    optionCount: optionCountOf(variable),
    partOf: variable.partOf,
    group: variable.group,
    mapped,
  });

  const mapped: PickableVariable[] = [];
  const takenPositions = new Set<string>();
  for (const [key, position] of Object.entries(design.variableMap)) {
    const variable = layout[position];
    if (!variable) continue;
    takenPositions.add(position);
    mapped.push(describe(key, position, variable, true));
  }

  const unmapped = Object.entries(layout)
    .filter(([position]) => !takenPositions.has(position))
    .map(([position, variable]) => describe(position, position, variable, false));

  const byPosition = (a: PickableVariable, b: PickableVariable) =>
    byDataPosition(a.position, b.position);
  return [...mapped.sort(byPosition), ...unmapped.sort(byPosition)];
}

// Exhaustive on purpose: a new report type whose reportExtraData names its variable something
// other than `variableKeys` used to fall through the old ternary and read undefined, which is not
// a type error anywhere. The `never` default turns that into a compile failure instead.
export function blockVariableKeys(block: PageSection): string[] {
  switch (block.reportType) {
    // Chart no question at all: they carry figures about the study, not answers from it.
    case 'study-overview':
    case 'headline-stats':
    // Every word of it is written; it takes no question at all.
    case 'stage-explainer':
    // Writing, or a picture: content, not answers.
    case 'text':
    case 'image':
      return [];
    // Both, depending on how it was set up: a dial with a question computes its score from the
    // answers, one without is a figure somebody typed. Returning nothing rather than one empty key
    // is what keeps the second kind working -- the block host reads a key list of length zero as
    // "this module charts nothing" and anything else as "a question is missing", and every dial
    // written before this field existed has none.
    case 'kpi-score':
      return block.reportExtraData.variableKey ? [block.reportExtraData.variableKey] : [];
    // One open-end, named like any other charted question so it resolves through variableMap and
    // shows in the picker -- it is simply drawn as text, or counted as words, rather than tallied
    // as answers.
    case 'open-end-explorer':
    case 'word-cloud':
    // One image-highlighter question, keyed by the question so all its click categories come with it.
    case 'heatmap':
    case 'pie-chart':
    case 'donut-chart':
    case 'filter-comparison':
    // small-multiples draws this one variable in every panel; `groupBy` decides the panels and
    // is a filter rather than something charted, so it is deliberately not returned here.
    case 'subgroup-panels':
    // Same shape: one measure, split by a second question. The grouping variable is the split
    // rather than something charted, so it is deliberately not returned here either.
    case 'segment-tiles':
    // And again: the rows are this one question, the columns are subgroups of respondents rather
    // than questions in their own right.
    case 'crosstab':
      return [block.reportExtraData.variableKey];
    case 'bar-chart':
      return block.reportExtraData.variableKeys;
    case 'action-list':
      return [];
    case 'stacked-scale':
    case 'funnel':
    case 'diverging-bars':
    // One row of the ranking grid per attribute; the ranks are its answers, not questions of
    // their own.
    case 'tiered-ranking':
    // Exactly two, and the order is the axes: the first is horizontal, the second vertical.
    case 'scatter-plot':
      return block.reportExtraData.variableKeys;
    // The attributes first, then the ranking grid behind the priority column. The block slices them
    // apart by its own `variableKeys.length`, so the order here is the contract between the two.
    case 'ownership-matrix':
      return [...block.reportExtraData.variableKeys, ...block.reportExtraData.priorityKeys ?? []];
    // One question per row, all sharing the answer list that becomes the rungs.
    case 'stage-matrix':
      return block.reportExtraData.variableKeys;
    default:
      return assertNever(block);
  }
}

/**
 * Whether a type charts a list of variables rather than exactly one -- i.e. whether the editor
 * offers to add panels to it. Kept beside blockVariableKeys so the two cannot disagree.
 */
export function acceptsMultipleVariables(reportType: ReportType): boolean {
  switch (reportType) {
    case 'bar-chart':
    case 'stacked-scale':
    case 'funnel':
    case 'diverging-bars':
    case 'tiered-ranking':
    case 'scatter-plot':
    case 'ownership-matrix':
    case 'stage-matrix':
      return true;
    case 'pie-chart':
    case 'donut-chart':
    case 'filter-comparison':
    case 'subgroup-panels':
    case 'segment-tiles':
    case 'crosstab':
    case 'study-overview':
    case 'headline-stats':
    case 'kpi-score':
    case 'action-list':
    case 'open-end-explorer':
    case 'word-cloud':
    case 'heatmap':
    case 'stage-explainer':
    case 'text':
    case 'image':
      return false;
    default:
      return assertNever(reportType);
  }
}

/**
 * Modules a Compare by breakout leaves alone -- they describe the study rather than answering a
 * question, so one copy per subgroup would be the same content repeated. They render once, across
 * the whole breakout, in their normal position.
 *
 * An exhaustive switch rather than a list of the exempt ones, matching the predicates either side:
 * a plain array would let a new module default into breaking out without anyone deciding.
 *
 * Takes the block rather than its type because one module answers both ways: see the dial below.
 */
export function ignoresCompareBy(block: PageSection): boolean {
  const reportType = block.reportType;
  switch (reportType) {
    case 'study-overview':
    case 'headline-stats':
    case 'action-list':
    // An explanation of how to read the deck is the same explanation for every subgroup.
    case 'stage-explainer':
    // As is any writing or picture: the same content whichever subgroup is reading.
    case 'text':
    case 'image':
      return true;
    // A typed figure repeated down a row of subgroups is the same number four times, which is what
    // this function exists to prevent. A computed one is a different number per subgroup, which is
    // what a breakout is for -- and left exempt it would sit among split charts showing the whole
    // sample, reading as a comparison it is not part of.
    case 'kpi-score':
      return blockVariableKeys(block).length === 0;
    case 'bar-chart':
    case 'pie-chart':
    case 'donut-chart':
    case 'filter-comparison':
    case 'subgroup-panels':
    case 'segment-tiles':
    case 'crosstab':
    case 'stacked-scale':
    // A funnel of one subgroup against another is a fair comparison -- the stages are the same
    // journey, and how much of it each subgroup completes is exactly the question.
    case 'funnel':
    // Which side of a trade-off each subgroup comes down on is the same kind of question.
    case 'diverging-bars':
    // And which attributes each subgroup ranks highest -- the tiers are recut per subgroup, which
    // is exactly the comparison someone breaking this out is asking for.
    case 'tiered-ranking':
    // What each subgroup said in their own words is the same question asked of each of them, and
    // reading two columns of quotes side by side is a fair use of a breakout.
    case 'open-end-explorer':
    // And which words each subgroup reached for. The clouds are not comparable by word size across
    // columns -- each scales to its own busiest word -- but which words appear at all is the read.
    case 'word-cloud':
    // And where on the creative each subgroup looked.
    case 'heatmap':
    // Where each subgroup places the same items is the comparison a positioning map is for.
    case 'scatter-plot':
    // And which brands each subgroup associates with what.
    case 'ownership-matrix':
    // And where each subgroup sits on the ladder.
    case 'stage-matrix':
      return false;
    default:
      return assertNever(reportType);
  }
}

/** Whether a type splits into panels by a second question's options. */
export function acceptsGroupBy(reportType: ReportType): boolean {
  switch (reportType) {
    case 'subgroup-panels':
    case 'segment-tiles':
    case 'crosstab':
      return true;
    case 'bar-chart':
    case 'pie-chart':
    case 'donut-chart':
    case 'filter-comparison':
    case 'study-overview':
    case 'headline-stats':
    case 'kpi-score':
    case 'action-list':
    case 'stacked-scale':
    case 'funnel':
    case 'diverging-bars':
    case 'tiered-ranking':
    case 'open-end-explorer':
    case 'word-cloud':
    case 'heatmap':
    case 'scatter-plot':
    case 'ownership-matrix':
    case 'stage-matrix':
    case 'stage-explainer':
    case 'text':
    case 'image':
      return false;
    default:
      return assertNever(reportType);
  }
}

/** The variable whose options split a block into panels, for the types that have one. */
export function blockGroupByKey(block: PageSection): string | undefined {
  return block.reportType === 'subgroup-panels' || block.reportType === 'segment-tiles'
    || block.reportType === 'crosstab'
    ? block.reportExtraData.groupBy
    : undefined;
}

function applySettings(
  points: ChartPoint[],
  sortDescending?: boolean,
  topN?: number,
  /**
   * What to rank on. The count is right for one question, but across several with different bases a
   * count of 40 is a third of one and a twentieth of another -- so a shared axis has to rank on
   * something comparable. See barSeries.
   */
  rank: (point: ChartPoint) => number = point => point.value,
): ChartPoint[] {
  const ordered = sortDescending ? [...points].sort((a, b) => rank(b) - rank(a)) : [...points];
  return topN && topN > 0 ? ordered.slice(0, topN) : ordered;
}

export const NOT_ASKED_LABEL = 'Not asked';

// The two series a categorical-distribution block always renders. Exported so the block, the
// fixtures and any copy describing the chart cannot drift apart.
export const TOTAL_SERIES_NAME = 'Total Overall';
export const FILTERED_SERIES_NAME = 'Dynamic to Filter';

export function paletteColors(palette: ColorPalette): string[] {
  return [palette.primary, palette.secondary, palette.accent, palette.positive, palette.negative];
}

/** How far each successive variant moves toward white or black. */
const SHADE_STEP = 0.26;

/**
 * A colour as `#rrggbb`, from the forms a picker or a person writes one in: `#rgb`, `#rrggbb`,
 * `#rrggbbaa` (its opacity dropped -- the colour maths here is about the colour, not how see-through
 * it is) and `rgb()`/`rgba()`. Null for anything else, a named colour or a CSS variable.
 *
 * The colour picker hands back eight digits the moment its opacity slider is off 100%, and a typed
 * `#fff` is the usual way to write white; read as six digits only, both made every contrast check
 * fail and the deck's text fall back to its plain defaults.
 */
export function toHex6(value: string): string | null {
  const text = (value ?? '').trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(text)) return text;
  if (/^#[0-9a-f]{8}$/.test(text)) return text.slice(0, 7);
  if (/^#[0-9a-f]{3,4}$/.test(text)) return '#' + [...text.slice(1, 4)].map(digit => digit + digit).join('');
  const rgb = text.match(/^rgba?\(\s*(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})/);
  if (rgb) {
    return '#' + rgb.slice(1, 4)
      .map(channel => Math.min(255, Number(channel)).toString(16).padStart(2, '0'))
      .join('');
  }
  return null;
}

function mix(value: string, toward: number, amount: number): string {
  const hex = toHex6(value);
  if (!hex) return value;
  const channel = (i: number) => {
    const from = parseInt(hex.slice(i, i + 2), 16);
    return Math.round(from + (toward - from) * amount).toString(16).padStart(2, '0');
  };
  return `#${channel(1)}${channel(3)}${channel(5)}`;
}

/**
 * Perceived brightness, 0-255. The standard luma weights: the eye reads green as far brighter than
 * blue at the same value, so a plain channel average would call navy and olive the same darkness.
 */
export function luminance(value: string): number {
  const hex = toHex6(value);
  if (!hex) return 255;
  const [r, g, b] = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

/**
 * How far apart two colours read, as WCAG measures it: 1 for the same colour, 21 for black on white.
 * 4.5 is the floor for body text, 3 for large or bold text. Null where either is not a plain hex.
 */
export function contrastRatio(a: string, b: string): number | null {
  const relative = (value: string): number | null => {
    const hex = toHex6(value);
    if (!hex) return null;
    const [r, g, bl] = [1, 3, 5].map(i => {
      const channel = parseInt(hex.slice(i, i + 2), 16) / 255;
      return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * bl;
  };
  const [first, second] = [relative(a), relative(b)];
  if (first === null || second === null) return null;
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}

/**
 * Which of a deck's two text colours to print on a fill: whichever reads better against it.
 *
 * Always one of the two, even where neither reads well. It used to fall back to plain white or
 * near-black there, which overrode the colours the report was given: a pale light text on a
 * mid-tone fill came out white, and looked as though the setting had been ignored. The colours are
 * the client's to choose, readable or not.
 */
export function readableInk(fill: string, dark: string, light: string): string {
  const onDark = contrastRatio(fill, dark) ?? 0;
  const onLight = contrastRatio(fill, light) ?? 0;
  return onDark >= onLight ? dark : light;
}

/**
 * `hex` taken to a given brightness, keeping its hue: the deck's subsection background, rules and inert
 * greys are the palette's own colour at a fixed lightness rather than fixed greys of their own.
 *
 * This is how the prototype's neutrals were actually built, which is only visible once the numbers
 * are checked: its `#eef3f5` background is its `#13455B` navy at brightness 241.7, its `#dbe4e8`
 * rules the same navy at 225.7, its `#c3ced3` at 203.3. Deriving them means a deck whose palette is
 * warm gets a warm background instead of this one's cold blue-grey.
 *
 * **Brightness is pinned, not the mix amount**, and that is the part that matters: a pale palette
 * is shaded *down* to reach the target where a dark one is tinted *up*, so the contrast between
 * background, rules and the white cards on them survives any palette. A fixed `tint(primary, 0.93)`
 * would collapse to near-white the moment someone picked a pale yellow.
 *
 * Both directions are exact rather than iterative, because brightness is linear in the mix.
 */
export function neutralFrom(hex: string, target: number): string {
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return hex;
  const from = luminance(hex);
  if (from < target) {
    const headroom = 255 - from;
    return headroom <= 0 ? hex : mix(hex, 255, Math.min(1, (target - from) / headroom));
  }
  return from <= 0 ? hex : mix(hex, 0, Math.min(1, (from - target) / from));
}

/**
 * The three surface brightnesses, read off the prototype. Named for what they are rather than for
 * the colours they happen to produce under the default palette.
 */
export const SURFACE_LUMINANCE = {
  /** The subsection behind the cards, and bar tracks. */
  background: 241.7,
  /** Card borders and rules. */
  line: 225.7,
  /** Inert data -- unowned rungs, white-space columns. */
  inert: 203.3,
};

/**
 * `hex` moved toward white by `amount` (0 = unchanged, 1 = white).
 *
 * For the places that want the deck's colour present but not loud -- a banded table row behind
 * black text, where the palette itself would be a background nothing could be read on. Shares
 * `mix` with the chart shades so a tint and a chart colour cannot drift apart.
 */
export function tint(hex: string, amount: number): string {
  return mix(hex, 255, amount);
}

/**
 * `count` colours, all different. The palette's five roles come first; past those, each role returns
 * as a tint and then a shade of itself rather than as itself again -- a chart with six series used
 * to draw the sixth in the first one's colour, which reads as two things being the same thing.
 *
 * Derived from the index alone, so an answer option keeps its colour wherever it is drawn: the same
 * property `optionIndex` colouring already relies on across compare columns and small-multiple
 * panels. Wraps only after fifteen, where a chart has bigger problems than a repeated colour.
 */
export function chartColors(palette: ColorPalette, count: number): string[] {
  const roles = paletteColors(palette);
  const colors: string[] = [];

  for (let i = 0; i < Math.max(0, count); i++) {
    const role = roles[i % roles.length];
    // 0 = the palette itself, then alternating lighter and darker, each further from the original.
    const round = Math.floor(i / roles.length);
    if (round === 0) {
      colors.push(role);
      continue;
    }
    const lighter = round % 2 === 1;
    const step = Math.ceil(round / 2) * SHADE_STEP;
    colors.push(mix(role, lighter ? 255 : 0, Math.min(0.75, step)));
  }

  return colors;
}

/**
 * The answers of one question that count towards a single figure.
 *
 * Shared because several modules reduce a whole question to one number -- a market tile, a funnel
 * stage, a crosstab cell -- and every one of them has to answer "which answer?". A question has
 * several; the figure has room for one. Naming them here rather than per module keeps one spelling
 * of the setting and one rule for what an unset one means.
 *
 * Unset falls back to the first answer as asked. That is right for a yes/no and wrong the moment
 * a scale is asked from its unfavourable end, so it is a floor that keeps a block rendering rather
 * than a default anyone should rely on -- which is why the editor prints back what it settled on.
 *
 * An empty list is not unset: it is someone having unticked every answer, and it counts nothing.
 * Reading it as the fallback would keep a figure on screen for an answer the editor shows unticked,
 * and the next answer ticked would then be summed with one nobody chose.
 *
 * Only real answers: "Not asked" is the slice of a compare column that never saw the question, and
 * summing it into a metric would report the gap in the sample as though it were a finding.
 */
export function countedPoints(points: ChartPoint[], answers?: string[]): ChartPoint[] {
  const real = points.filter(point => point.optionIndex >= 0);
  if (!answers) return real.slice(0, 1);

  const named = new Set(answers);
  return real.filter(point => named.has(point.label));
}

/** `countedPoints`, summed -- the figure itself, with the respondents behind it. */
export function countedFigure(
  points: ChartPoint[],
  answers?: string[],
): { percent: number; value: number } {
  return countedPoints(points, answers).reduce(
    (total, point) => ({ percent: total.percent + point.percent, value: total.value + point.value }),
    { percent: 0, value: 0 });
}

/**
 * Rebased onto everyone in the containing group, so the answers and "Not asked" read down to 100%.
 * Off each point's own share and the unrounded `exactBase` rather than its count: weighted counts
 * are rounded for display, and rebasing rounded counts onto a rounded group size left a weighted
 * column short of 100%.
 */
function withNotAsked(points: ChartPoint[], base: number, contextN?: number, exactBase = base): ChartPoint[] {
  if (contextN === undefined || !(exactBase > 0) || contextN <= exactBase + 1e-9) return points;
  const rebase = exactBase / contextN;
  return [
    ...points.map(point => ({ ...point, percent: point.percent * rebase })),
    { label: NOT_ASKED_LABEL, value: Math.round(contextN - exactBase), percent: (1 - rebase) * 100, optionIndex: -1 },
  ];
}

/**
 * Reorders and zero-fills `points` onto `spine`. `aggregate()` drops zero-count options entirely,
 * so a series whose subgroup never picked an option would otherwise be shorter than its
 * neighbours -- and every chart here builds its category axis from one series while drawing
 * several. Matching is by label: for codes absent from the datamap `optionIndex` is assigned by
 * discovery order and so is not comparable between record sets, whereas the label is the category
 * the reader sees and is identical in both.
 */
function alignedTo(spine: ChartPoint[], points: ChartPoint[]): ChartPoint[] {
  const byLabel = new Map(points.map(point => [point.label, point]));
  return spine.map(point => byLabel.get(point.label)
    ?? { label: point.label, value: 0, percent: 0, optionIndex: point.optionIndex });
}

/**
 * A coded question's points with every option the datamap lists, in asked order, the unpicked ones
 * at zero. Codes the datamap does not list stay at the end, as `aggregate()` places them. Anything
 * that is not a coded question has no option list to fill against and is returned as it is.
 */
function zeroFilled(
  points: ChartPoint[],
  key: string,
  variableMap: Record<string, string> = {},
  layout: SurveyLayout = {},
): ChartPoint[] {
  const variable = variableOf(key, variableMap, layout);
  if (!variable || variable.kind !== 'coded') return points;

  const spine = variable.options.map((option, optionIndex) =>
    ({ label: option.label, value: 0, percent: 0, optionIndex }));
  const listed = new Set(spine.map(point => point.label));
  return [...alignedTo(spine, points), ...points.filter(point => !listed.has(point.label))];
}

/**
 * The Total Overall / Dynamic to Filter pair. The overall aggregate is the category spine: sorting
 * and topN are applied to it once and the filtered series is reordered to match, because sorting the
 * two independently would put different categories at the same axis position. Zero-filling is
 * handled by alignedTo above.
 *
 *
 * `contextN` (compare mode's "Not asked" rebase) is deliberately ignored here. It rebases percentages
 * onto one containing group, and this chart's whole point is that its two series have different bases.
 */
function distributionSeries(
  key: string,
  overall: VariableAggregate,
  filtered: VariableAggregate,
  sortDescending?: boolean,
  topN?: number,
  variableMap?: Record<string, string>,
  layout?: SurveyLayout,
): ChartSeries[] {
  // Nothing to compare against, and the host renders series names as the missing-variable list --
  // "No data for Total Overall, Dynamic to Filter" would name the legend instead of the question.
  if (!overall.known) {
    return [{
      name: variableLabel(key, variableMap, layout),
      points: [],
      base: 0,
      multiResponse: false,
      known: false,
    }];
  }

  const spine = applySettings(overall.points, sortDescending, topN);

  return [
    {
      name: TOTAL_SERIES_NAME,
      points: spine,
      base: overall.base,
      multiResponse: overall.multiResponse,
      known: true,
      truncated: spine.length < overall.points.length
        ? { shown: spine.length, total: overall.points.length }
        : undefined,
    },
    {
      name: FILTERED_SERIES_NAME,
      points: alignedTo(spine, filtered.points),
      base: filtered.base,
      multiResponse: filtered.multiResponse,
      known: true,
    },
  ];
}

export const TOTAL_GROUP_NAME = 'Total';

/**
 * One series per panel of a small-multiples grid: the same question aggregated once for every
 * option of `groupBy`, optionally behind an all-respondents Total.
 *
 * Group filters stack on top of whatever the deck's rail is already filtering -- `records` arrives
 * already filtered -- which matches how compare mode treats its subgroups. Options come from
 * filterOptions, so a group with nobody in the current filter simply does not get a panel rather
 * than getting an empty one.
 *
 * Every panel is aligned to one spine and sorted/truncated once, because the panels are only
 * comparable if the same category sits in the same position in each. The spine is the Total when
 * there is one (it has every option any group can have) and otherwise the widest group.
 */
/** One panel's worth of respondents: an answer to a question, named as the panel is labelled. */
export interface Subgroup {
  variableKey: string;
  /** The answer's label, as the datamap has it -- the same currency `countedAnswers` trades in. */
  answer: string;
}

/**
 * The groups a module splits into, as records.
 *
 * Two ways of saying it, because two different things get asked for. `groupBy` names one question
 * and every answer becomes a group, which is the common case and needs one choice. `subgroups`
 * names the groups one at a time and they need not come from the same question at all -- "Female",
 * "Male", "45+" is three panels drawn from two questions, which no single `groupBy` can express.
 *
 * `subgroups` wins where it is set. Matching is by answer label rather than by code: a label is
 * what the design can carry legibly and what the reader sees on the panel, and codes are not stable
 * across a datamap refresh.
 */
function orderedByQuestion(subgroups: readonly Subgroup[]): Subgroup[] {
  // A Map keyed by question: insertion order makes the question's *first* panel decide where the
  // whole question sits, so a list that was already grouped comes back untouched and only an
  // interleaved one moves.
  const byQuestion = new Map<string, Subgroup[]>();
  for (const subgroup of subgroups) {
    const answers = byQuestion.get(subgroup.variableKey);
    if (answers) answers.push(subgroup);
    else byQuestion.set(subgroup.variableKey, [subgroup]);
  }
  return [...byQuestion.values()].flat();
}

interface RecordGroup {
  label: string;
  /** The question this group is an answer of. */
  variableKey: string;
  records: RespondentRecord[];
}

function groupRecords(
  settings: { groupBy?: string; subgroups?: Subgroup[] },
  context: SeriesContext,
): RecordGroup[] {
  const { records = [], variableMap = {}, layout = {} } = context;

  if (settings.subgroups?.length) {
    // Grouped by question before anything is drawn, because the stored list need not be. Adding a
    // question appends all of its answers, but re-ticking one panel appends only that one, and an
    // agent edit can write them in any order at all -- so "Female", "Male", "45+" can reach here as
    // "Female", "45+", "Male" and read as one list of three rather than two questions. The panels
    // are the same set either way; this only fixes where each sits.
    return orderedByQuestion(settings.subgroups)
      .map(subgroup => {
        const option = filterOptions(records, subgroup.variableKey, variableMap, layout)
          .find(candidate => candidate.label === subgroup.answer);
        if (!option) return null;
        return {
          label: option.label,
          variableKey: subgroup.variableKey,
          records: applyFilters(
            records, { [subgroup.variableKey]: String(option.value) }, variableMap, layout),
        };
      })
      .filter((group): group is RecordGroup => !!group);
  }

  const groupBy = settings.groupBy;
  if (!groupBy) return [];
  return filterOptions(records, groupBy, variableMap, layout).map(option => ({
    label: option.label,
    variableKey: groupBy,
    records: applyFilters(records, { [groupBy]: String(option.value) }, variableMap, layout),
  }));
}

/**
 * Respondents who gave any of the listed answers -- answers of one question are alternatives, and
 * every question named must hold. An answer the data no longer has selects nobody rather than being
 * ignored: a table filtered to a group that has gone should say it is empty, not quietly widen.
 */
function withinSubgroups(
  records: RespondentRecord[],
  subgroups: readonly Subgroup[],
  context: SeriesContext,
): RespondentRecord[] {
  const { variableMap = {}, layout = {} } = context;
  const byQuestion = new Map<string, string[]>();
  for (const { variableKey, answer } of subgroups) {
    byQuestion.set(variableKey, [...byQuestion.get(variableKey) ?? [], answer]);
  }

  let kept = records;
  for (const [variableKey, answers] of byQuestion) {
    const options = filterOptions(kept, variableKey, variableMap, layout)
      .filter(option => answers.includes(option.label));
    const matched = new Set<RespondentRecord>();
    for (const option of options) {
      applyFilters(kept, { [variableKey]: String(option.value) }, variableMap, layout)
        .forEach(record => matched.add(record));
    }
    kept = kept.filter(record => matched.has(record));
  }
  return kept;
}

interface Panel {
  label: string;
  /** The question a group came out of; absent on the Total. */
  variableKey?: string;
  records: RespondentRecord[];
  aggregate: VariableAggregate;
}

interface PanelSet {
  panels: Panel[];
  spine: ChartPoint[];
  /** How many answers there were before a top-N cut the spine. */
  spineTotal: number;
}

/**
 * The panels of a grouped module with the respondents behind each, and the one spine every panel is
 * aligned to. Null when the question itself does not resolve. Shared by the charts and the table, so
 * a figure in a crosstab and the same figure in the panels beside it are the same figure.
 */
function groupedPanels(
  settings: {
    variableKey: string;
    groupBy?: string;
    subgroups?: Subgroup[];
    includeTotal?: boolean;
    sortDescending?: boolean;
    topN?: number;
  },
  context: SeriesContext,
): PanelSet | null {
  const { variableKey, includeTotal, sortDescending, topN } = settings;
  const { records = [], variableMap = {}, layout = {}, weightOf } = context;

  const totalAggregate = aggregate(records, variableKey, variableMap, layout, weightOf);
  if (!totalAggregate.known) return null;

  const groups: Panel[] = groupRecords(settings, context)
    .map(group => ({
      ...group,
      aggregate: aggregate(group.records, variableKey, variableMap, layout, weightOf),
    }))
    // A group that exists in the sample but where nobody answered the charted question -- a skipped
    // question, a rarely-picked demographic -- would render as an empty panel saying nothing while
    // taking a grid cell. filterOptions can only tell that the group exists, not that it answered.
    // Read off the head count, not the weighted base: a group of one light respondent rounds to 0.
    .filter(group => (group.aggregate.sampleBase ?? group.aggregate.base) > 0);

  const panels = includeTotal === false
    ? groups
    : [{ label: TOTAL_GROUP_NAME, records, aggregate: totalAggregate }, ...groups];
  if (!panels.length) return { panels, spine: [], spineTotal: 0 };

  const widest = panels.reduce(
    (best, panel) => panel.aggregate.points.length > best.aggregate.points.length ? panel : best,
    panels[0]);
  const spineSource = includeTotal === false ? widest.aggregate : totalAggregate;
  return {
    panels,
    spine: applySettings(spineSource.points, sortDescending, topN),
    spineTotal: spineSource.points.length,
  };
}

/** The host renders series names as its missing-variable list, so this names the question. */
function unknownSeries(variableKey: string, context: SeriesContext): ChartSeries {
  return {
    name: variableLabel(variableKey, context.variableMap, context.layout),
    points: [],
    base: 0,
    multiResponse: false,
    known: false,
  };
}

function groupedSeries(
  settings: {
    variableKey: string;
    groupBy?: string;
    subgroups?: Subgroup[];
    includeTotal?: boolean;
    sortDescending?: boolean;
    topN?: number;
  },
  context: SeriesContext,
): ChartSeries[] {
  const set = groupedPanels(settings, context);
  if (!set) return [unknownSeries(settings.variableKey, context)];

  return set.panels.map(panel => ({
    name: panel.label,
    points: alignedTo(set.spine, panel.aggregate.points),
    base: panel.aggregate.base,
    multiResponse: panel.aggregate.multiResponse,
    known: true,
    numeric: panel.aggregate.numeric,
    truncated: set.spine.length < set.spineTotal
      ? { shown: set.spine.length, total: set.spineTotal }
      : undefined,
  }));
}

/** What a significance test found for one cell. */
export interface CrosstabMark {
  /** The letters of the columns, from the same question, this cell is significantly higher than. */
  higherThan: string[];
  /** Against every respondent outside this column. */
  versusRest: Direction;
}

/** A net or a mean, in one column. */
export interface CrosstabSummaryCell {
  /** A weighted count for a net, the mean itself for a mean. */
  value: number;
  /** A net's share of the column; the mean again for a mean. */
  percent: number;
  /** Respondents behind it, unweighted -- what a guardrail or a drill reads. */
  count: number;
  mark: CrosstabMark;
}

/** One answer of the table's third variable, which a slice of the table is filtered to. */
export interface CrosstabSlice {
  variableKey: string;
  label: string;
  /** Respondents in the slice, unweighted. */
  sampleBase: number;
}

/** The deck's settings a crosstab prints and tests by, the same on every column of one block. */
export interface CrosstabTableSettings {
  weighted: boolean;
  confidenceLevel: 90 | 95 | 99;
  minBase: number;
  cautionBase: number;
  minCellCount: number;
  correction: Correction;
  decimals: 0 | 1 | 2;
  /** Respondents left after the table's own filter, when it has one; null when it has none. */
  filteredTo: number | null;
  /** Respondents in the whole table, unweighted, before any slice: the n a drill starts from. */
  respondents: number;
  /** Questions whose columns share respondents, so are not tested against each other. */
  overlapping: string[];
  /** How the slices are shown, when the table has a third variable. */
  layerView: 'nested' | 'tabs' | 'grid' | null;
}

/**
 * What a crosstab column carries beyond its figures: who it is, how big it really is, and what the
 * tests said about each of its cells. Aligned to the series' `points`, row for row.
 */
export interface CrosstabColumn {
  /** The letter other columns' marks name it by. Empty on a Total. Restarts in each slice's table. */
  letter: string;
  total: boolean;
  /** The question this column is an answer of. Absent on a Total. */
  variableKey?: string;
  /** What the banner row over it reads, when there is one. */
  questionLabel?: string;
  /** Which answer of the third variable this column belongs to, when the table has one. */
  slice: CrosstabSlice | null;
  /** Respondents answering, unweighted. */
  sampleBase: number;
  /** What the column's base is worth once weighting's loss of precision is counted. */
  effectiveBase: number;
  /** Under the hard threshold: greyed, untested, not drillable. */
  lowBase: boolean;
  /** Under the soft threshold but not the hard one: shown with a caution marker. */
  caution: boolean;
  /** Respondents behind each row's figure, unweighted. */
  counts: number[];
  marks: CrosstabMark[];
  /** One per `nets` entry on the block, in its order. */
  nets: CrosstabSummaryCell[];
  /** Null where the block asks for no mean, or the column has nobody to average. */
  mean: CrosstabSummaryCell | null;
  /**
   * Against everyone else, for significance shading: the answer rows, then the nets, then the mean.
   * Empty unless the block shades by significance.
   */
  shades: Direction[];
  /** The answers that pick out this column's respondents: what drilling into it filters to. */
  selection: Subgroup[];
  table: CrosstabTableSettings;
}

const NO_MARK: CrosstabMark = { higherThan: [], versusRest: null };

/** The weight of `records` that gave any of `answers` to the question. */
function netWeight(
  records: RespondentRecord[],
  variable: SurveyVariable,
  answers: ReadonlySet<string>,
  weigh: WeightOf,
): number {
  if (variable.kind === 'multi') {
    const columns = variable.columns.filter(column => answers.has(column.label));
    return records.reduce((sum, record) =>
      columns.some(column => isSelected(record[column.column])) ? sum + weigh(record) : sum, 0);
  }
  if (variable.kind !== 'coded') return 0;

  const labelOf = new Map(variable.options.map(option => [String(option.value), option.label]));
  return records.reduce((sum, record) =>
    codesOf(record, variable.column).some(code => answers.has(labelOf.get(code) ?? code))
      ? sum + weigh(record)
      : sum, 0);
}

/**
 * The mean of a rating scale, each answer scored by its code. Single answers only: a respondent who
 * gave several codes to a scale has no one place on it. Answers in `excluded`, and codes that are not
 * numbers, score nothing and stay out of the base.
 */
function scaleMean(
  records: RespondentRecord[],
  variable: SurveyVariable,
  excluded: ReadonlySet<string>,
  weigh: WeightOf,
): MeanStats | null {
  if (variable.kind !== 'coded') return null;
  const labelOf = new Map(variable.options.map(option => [String(option.value), option.label]));
  const values: { value: number; weight: number }[] = [];
  for (const record of records) {
    const codes = codesOf(record, variable.column);
    if (codes.length !== 1) continue;
    if (excluded.has(labelOf.get(codes[0]) ?? codes[0])) continue;
    const score = Number(codes[0]);
    if (Number.isFinite(score)) values.push({ value: score, weight: weigh(record) });
  }
  return meanStats(values);
}

const OFF_SCALE = /don.?t know|not sure|unsure|not applicable|n\/a|prefer not|refused|no opinion|none of (these|the above)/i;

/**
 * Answers that are not on a rating scale: named as such ("Don't know", "Not applicable"), or coded
 * apart from the run of codes the scale itself uses -- a 9 or a 99 after a 1-5. What a mean leaves
 * out when nobody has chosen, because averaging a "Don't know" coded 9 into a five-point scale lifts
 * every mean by the share who said it. Exported so the editor pre-ticks the same answers.
 */
export function offScaleAnswers(options: readonly AnswerOption[]): string[] {
  const coded = options
    .map(option => ({ label: option.label, code: Number(option.value) }))
    .filter(option => Number.isFinite(option.code))
    .sort((a, b) => a.code - b.code);
  const named = new Set(options.filter(option => OFF_SCALE.test(option.label)).map(option => option.label));
  // The scale is the longest run of consecutive codes among the answers not named off it.
  const onScale = coded.filter(option => !named.has(option.label));
  let best: typeof onScale = [];
  let run: typeof onScale = [];
  for (const option of onScale) {
    run = run.length && option.code === run[run.length - 1].code + 1 ? [...run, option] : [option];
    if (run.length > best.length) best = run;
  }
  const scale = new Set(best.map(option => option.label));
  return options.filter(option => named.has(option.label) || (best.length > 1 && !scale.has(option.label)))
    .map(option => option.label);
}

/** One item of a numeric battery, as a mean with what a test needs. */
function itemMean(records: RespondentRecord[], column: string, weigh: WeightOf): MeanStats | null {
  const values: { value: number; weight: number }[] = [];
  for (const record of records) {
    const n = numberOf(record[column]);
    if (n !== null) values.push({ value: n, weight: weigh(record) });
  }
  return meanStats(values);
}

/**
 * Everyone in the table but one column, as a mean -- worked out from the Total and the column rather
 * than from records, because the Total already holds both and the column is a part of it.
 */
function meanOfRest(total: MeanStats, part: MeanStats): MeanStats | null {
  const sumWeights = total.sumWeights - part.sumWeights;
  const sumSquares = total.sumSquares - part.sumSquares;
  if (sumWeights <= 1e-9 || sumSquares <= 1e-9) return null;
  const secondMoment = (stats: MeanStats) => stats.sumWeights * (stats.variance + stats.mean ** 2);
  const mean = (total.mean * total.sumWeights - part.mean * part.sumWeights) / sumWeights;
  return {
    mean,
    variance: Math.max(0, (secondMoment(total) - secondMoment(part)) / sumWeights - mean ** 2),
    sumWeights,
    sumSquares,
    effectiveBase: effectiveBase(sumWeights, sumSquares),
    count: total.count - part.count,
  };
}

/** One figure in one column, as the tests read it: a share or a mean, with its base. */
type Tested =
  | { kind: 'share'; share: number; sumWeights: number; sumSquares: number }
  | { kind: 'mean'; stats: MeanStats };

function zOf(a: Tested, b: Tested): number | null {
  if (a.kind === 'mean' && b.kind === 'mean') return meanZ(a.stats, b.stats);
  if (a.kind === 'share' && b.kind === 'share') {
    return proportionZ(
      a.share, effectiveBase(a.sumWeights, a.sumSquares),
      b.share, effectiveBase(b.sumWeights, b.sumSquares));
  }
  return null;
}

/** The same figure for everyone outside a column. Null where the column is the whole table. */
function restOf(total: Tested, part: Tested): Tested | null {
  if (total.kind === 'mean' && part.kind === 'mean') {
    const rest = meanOfRest(total.stats, part.stats);
    return rest && { kind: 'mean', stats: rest };
  }
  if (total.kind !== 'share' || part.kind !== 'share') return null;
  const sumWeights = total.sumWeights - part.sumWeights;
  const sumSquares = total.sumSquares - part.sumSquares;
  if (sumWeights <= 1e-9 || sumSquares <= 1e-9) return null;
  return {
    kind: 'share',
    share: (total.share * total.sumWeights - part.share * part.sumWeights) / sumWeights,
    sumWeights,
    sumSquares,
  };
}

/** A column of a table about to be tabulated, before any of its figures are worked out. */
interface ColumnSpec {
  panel: Panel;
  total: boolean;
  slice: CrosstabSlice | null;
  /** Columns are tested against each other only within one group: a question, within one slice. */
  group: string;
  questionLabel?: string;
}

/**
 * Marks every row of one table. `figures` holds a row per table row and an entry per column -- null
 * where the column has nothing to test, which includes every column under the hard threshold.
 *
 * Column tests run only within one group: "Male" against "Female" is the comparison a banner is built
 * for, while "Male" against "18-34" compares two overlapping groups of people and means nothing. Each
 * pair is tested once, and whichever side is higher takes the other's letter. Totals are never
 * marked -- they are what the others are read against.
 *
 * Every test of the table is collected first and only then judged, because a correction for
 * multiple comparisons has to know how many tests there were.
 */
function markTable(
  columns: readonly { letter: string; total: boolean; group: string }[],
  figures: readonly (readonly (Tested | null)[])[],
  test: 'columns' | 'total',
  totalIndex: number,
  level: 90 | 95 | 99,
  correction: Correction,
  /** Groups whose columns share respondents, which an independent-samples test cannot compare. */
  overlapping: ReadonlySet<string> = new Set(),
): CrosstabMark[][] {
  type Pending = { row: number; i: number; j: number; z: number };
  const pending: Pending[][] = figures.map((row, r) => {
    const tests: Pending[] = [];
    if (test === 'total') {
      const total = totalIndex >= 0 ? row[totalIndex] : null;
      row.forEach((figure, i) => {
        if (!figure || !total || columns[i].total) return;
        const rest = restOf(total, figure);
        const z = rest && zOf(figure, rest);
        if (z !== null && z !== undefined) tests.push({ row: r, i, j: -1, z });
      });
      return tests;
    }
    row.forEach((figure, i) => {
      if (!figure || columns[i].total) return;
      for (let j = i + 1; j < row.length; j++) {
        const against = row[j];
        if (!against || columns[j].total || columns[j].group !== columns[i].group) continue;
        if (overlapping.has(columns[i].group)) continue;
        const z = zOf(figure, against);
        if (z !== null) tests.push({ row: r, i, j, z });
      }
    });
    return tests;
  });

  const passed = significantTests(pending.map(row => row.map(t => twoTailedP(t.z))), level, correction);
  const marks: CrosstabMark[][] = figures.map(row => row.map(() => ({ higherThan: [], versusRest: null })));
  pending.forEach((row, r) => row.forEach((t, k) => {
    if (!passed.has(`${r}:${k}`)) return;
    if (t.j < 0) {
      marks[r][t.i].versusRest = t.z > 0 ? 'higher' : 'lower';
    } else if (t.z > 0) {
      marks[r][t.i].higherThan.push(columns[t.j].letter);
    } else {
      marks[r][t.j].higherThan.push(columns[t.i].letter);
    }
  }));
  // Letters in column order, whichever side of a pair found them.
  for (const row of marks) for (const mark of row) mark.higherThan.sort();
  return marks;
}

/**
 * One table's columns, worked out: figures aligned to the spine, unweighted counts, nets, a mean, and
 * the marks of every test. A sliced table shown as tabs or a grid is several of these, one per slice.
 */
function tabulate(
  specs: ColumnSpec[],
  spine: ChartPoint[],
  spineTotal: number,
  extra: ReportTypeDataMap['crosstab'],
  context: SeriesContext,
  table: CrosstabTableSettings,
  /**
   * Work the Total out but leave it out of the table. The 'total' test and significance shading
   * compare every column with everyone outside it, which is read off the Total -- so a table drawn
   * without one still needs it, or its arrows would silently never appear.
   */
  hideTotal = false,
): ChartSeries[] {
  const { variableMap = {}, layout = {}, weightOf } = context;
  const variable = variableOf(extra.variableKey, variableMap, layout);
  const weigh = weightOf ?? (() => 1);
  const test = extra.sigTest === 'columns' || extra.sigTest === 'total' ? extra.sigTest : null;

  let lettered = 0;
  const columns = specs.map(spec => {
    const aggregated = spec.panel.aggregate;
    const sampleBase = aggregated.sampleBase ?? aggregated.base;
    const points = alignedTo(spine, aggregated.points);
    // The head count behind each figure. Unweighted, it already is the count.
    const counted = weightOf
      ? alignedTo(spine, aggregate(spec.panel.records, extra.variableKey, variableMap, layout).points)
      : points;
    return {
      ...spec,
      letter: spec.total ? '' : columnLetter(lettered++),
      sampleBase,
      sumWeights: aggregated.sumWeights ?? sampleBase,
      sumSquares: aggregated.sumSquares ?? sampleBase,
      lowBase: sampleBase < table.minBase,
      caution: sampleBase >= table.minBase && sampleBase < table.cautionBase,
      points,
      counts: counted.map(point => aggregated.numeric ? sampleBase : Math.round(point.value)),
    };
  });
  const totalIndex = columns.findIndex(column => column.total);
  const testable = (i: number) => !columns[i].lowBase;

  // Columns of one question that share respondents -- a multi-punch banner, where someone can be in
  // "Uses A" and "Uses B" at once -- cannot be compared with an independent-samples test: the
  // variance would be wrong, and the letters would disagree with any overlap-aware tab package.
  // Such a group is left untested and said so, rather than lettered on a test that does not apply.
  const overlapping = new Set<string>();
  const seen = new Map<string, Set<RespondentRecord>>();
  for (const column of columns) {
    if (column.total) continue;
    const members = seen.get(column.group) ?? new Set<RespondentRecord>();
    for (const record of column.panel.records) {
      if (members.has(record)) overlapping.add(column.group);
      members.add(record);
    }
    seen.set(column.group, members);
  }
  for (const group of overlapping) {
    const column = columns.find(candidate => candidate.group === group);
    const name = column?.panel.variableKey ? variableLabel(column.panel.variableKey, variableMap, layout) : '';
    if (name && !table.overlapping.includes(name)) table.overlapping.push(name);
  }

  // Nets and means are read off the respondents rather than summed from the rows, because neither
  // is a sum of rows: a respondent picking two answers in a net is still one respondent in it.
  const nets = (extra.nets ?? []).filter(net => net.label?.trim() && net.answers?.length);
  const netFigures = nets.map(net => {
    const answers = new Set(net.answers);
    return columns.map(column => {
      const answered = variable ? answering(column.panel.records, variable) : [];
      const weight = variable ? netWeight(answered, variable, answers, weigh) : 0;
      return {
        value: Math.round(weight),
        share: column.sumWeights > 0 ? weight / column.sumWeights : 0,
        count: variable ? Math.round(netWeight(answered, variable, answers, () => 1)) : 0,
      };
    });
  });

  const excluded = new Set(extra.meanExcludes ?? (variable?.kind === 'coded' ? offScaleAnswers(variable.options) : []));
  const means = extra.showMean && variable?.kind === 'coded'
    ? columns.map(column => scaleMean(column.panel.records, variable, excluded, weigh))
    : null;

  // A numeric battery's rows are means already, so its tests are on means.
  const itemStats = variable?.kind === 'numeric'
    ? spine.map(point => {
      const item = variable.columns.find(column => column.label === point.label);
      return columns.map(column => item ? itemMean(column.panel.records, item.column, weigh) : null);
    })
    : null;

  const share = (i: number, value: number): Tested | null => testable(i)
    ? { kind: 'share', share: value, sumWeights: columns[i].sumWeights, sumSquares: columns[i].sumSquares }
    : null;
  const mean = (i: number, stats: MeanStats | null): Tested | null =>
    testable(i) && stats ? { kind: 'mean', stats } : null;
  // A battery item is tested on the respondents who answered that item, not the column's base:
  // an item three people answered is no more testable for sitting in a column of a hundred.
  const itemMeanTested = (i: number, stats: MeanStats | null): Tested | null =>
    stats && stats.count >= table.minBase ? mean(i, stats) : null;

  const figures: (Tested | null)[][] = [
    ...spine.map((_, row) => columns.map((column, i) => itemStats
      ? itemMeanTested(i, itemStats[row][i])
      : share(i, (column.points[row]?.percent ?? 0) / 100))),
    ...netFigures.map(row => row.map((figure, i) => share(i, figure.share))),
    ...(means ? [columns.map((_, i) => mean(i, means[i]))] : []),
  ];
  const marks = test
    ? markTable(columns, figures, test, totalIndex, table.confidenceLevel, table.correction, overlapping)
    : figures.map(row => row.map(() => NO_MARK));
  const markOf = (row: number, i: number) => marks[row]?.[i] ?? NO_MARK;
  // Significance shading reads every cell against everyone else, whichever test marks the cells.
  const shadeMarks = extra.shading === 'significance'
    ? test === 'total' ? marks : markTable(columns, figures, 'total', totalIndex, table.confidenceLevel, table.correction, overlapping)
    : null;

  const drawn = columns.map((column, i) => ({
    name: column.panel.label,
    points: column.points,
    base: column.panel.aggregate.base,
    multiResponse: column.panel.aggregate.multiResponse,
    known: true,
    numeric: column.panel.aggregate.numeric,
    truncated: spine.length < spineTotal ? { shown: spine.length, total: spineTotal } : undefined,
    crosstab: {
      letter: column.letter,
      total: column.total,
      variableKey: column.panel.variableKey,
      questionLabel: column.questionLabel,
      slice: column.slice,
      sampleBase: column.sampleBase,
      effectiveBase: effectiveBase(column.sumWeights, column.sumSquares),
      lowBase: column.lowBase,
      caution: column.caution,
      marks: spine.map((_, row) => markOf(row, i)),
      counts: itemStats ? spine.map((_, row) => itemStats[row][i]?.count ?? 0) : column.counts,
      nets: netFigures.map((row, n) => ({
        value: row[i].value,
        percent: row[i].share * 100,
        count: row[i].count,
        mark: markOf(spine.length + n, i),
      })),
      mean: means?.[i]
        ? {
          value: means[i]!.mean,
          percent: means[i]!.mean,
          count: means[i]!.count,
          mark: markOf(spine.length + netFigures.length, i),
        }
        : null,
      shades: shadeMarks ? shadeMarks.map(row => row[i]?.versusRest ?? null) : [],
      selection: [
        ...(column.slice ? [{ variableKey: column.slice.variableKey, answer: column.slice.label }] : []),
        ...(column.panel.variableKey ? [{ variableKey: column.panel.variableKey, answer: column.panel.label }] : []),
      ],
      table,
    },
  }));
  return hideTotal ? drawn.filter(s => !s.crosstab.total) : drawn;
}

/**
 * A crosstab: one series per column, exactly as the panelled modules have them, each carrying what
 * the table adds -- a letter, the unweighted and effective bases, nets, a mean, and the marks the
 * significance tests leave on every cell. The figures themselves come from `groupedPanels`, the
 * same implementation the charts use.
 *
 * With a third variable the table is sliced by its answers. Nested, the slices' columns sit side by
 * side under one Total in a single table; as tabs or a grid, each slice is a whole table of its own,
 * Total included, and the series come back one slice after another. Every slice is aligned to the
 * unsliced table's rows, so the same answer is on the same row in each.
 */
function crosstabSeries(
  extra: ReportTypeDataMap['crosstab'],
  context: SeriesContext,
): ChartSeries[] {
  const { variableMap = {}, layout = {} } = context;
  const records = extra.tableFilter?.length
    ? withinSubgroups(context.records ?? [], extra.tableFilter, context)
    : context.records ?? [];
  const scoped = { ...context, records };

  // Always with its Total, which the tests need whether or not the table shows it.
  const withTotal = { ...extra, includeTotal: true };
  const overall = groupedPanels(withTotal, scoped);
  if (!overall) return [unknownSeries(extra.variableKey, context)];

  const statistics = resolveStatistics(context.statistics);
  const hideTotal = extra.includeTotal === false;
  const layerBy = extra.layerBy && extra.layerBy !== extra.variableKey ? extra.layerBy : '';
  const slices = layerBy
    ? groupRecords({ groupBy: layerBy }, scoped).filter(slice => slice.records.length)
    : [];
  const view = slices.length ? extra.layerView ?? 'nested' : null;

  const table: CrosstabTableSettings = {
    weighted: !!context.weightOf,
    confidenceLevel: statistics.confidenceLevel,
    minBase: statistics.minBase,
    cautionBase: statistics.cautionBase,
    minCellCount: statistics.minCellCount,
    correction: statistics.correction,
    decimals: statistics.decimals,
    filteredTo: extra.tableFilter?.length ? records.length : null,
    respondents: variableOf(extra.variableKey, variableMap, layout)
      ? baseFor(records, variableOf(extra.variableKey, variableMap, layout)!)
      : records.length,
    overlapping: [],
    layerView: view,
  };

  const questions = new Set(overall.panels.map(panel => panel.variableKey).filter(Boolean));
  const banner = (key?: string) => key && questions.size > 1
    ? variableLabel(key, variableMap, layout)
    : undefined;
  const specsOf = (panels: Panel[], slice: CrosstabSlice | null, withTotal: boolean): ColumnSpec[] =>
    panels.map((panel, index) => ({
      panel,
      total: withTotal && index === 0,
      slice,
      group: `${slice?.label ?? ''}\u0000${panel.variableKey ?? ''}`,
      questionLabel: banner(panel.variableKey),
    }));
  const rowVariable = variableOf(extra.variableKey, variableMap, layout);
  const sliceOf = (group: RecordGroup): CrosstabSlice => ({
    variableKey: layerBy,
    label: group.label,
    // Who in the slice answered the row question: the slice's Base, and what a drill into it keeps.
    sampleBase: rowVariable ? baseFor(group.records, rowVariable) : group.records.length,
  });

  if (!view) {
    return tabulate(specsOf(overall.panels, null, true), overall.spine, overall.spineTotal,
      extra, scoped, table, hideTotal);
  }

  if (view === 'nested') {
    const head = specsOf(overall.panels.slice(0, 1), null, true);
    const body = slices.flatMap(group => {
      const set = groupedPanels({ ...extra, includeTotal: false }, { ...scoped, records: group.records });
      const slice = sliceOf(group);
      return specsOf(set?.panels ?? [], slice, false).map(spec => ({
        ...spec,
        // The banner row names the slice, and the question too where the columns come from several.
        questionLabel: spec.questionLabel ? `${slice.label} · ${spec.questionLabel}` : slice.label,
      }));
    });
    return tabulate([...head, ...body], overall.spine, overall.spineTotal, extra, scoped, table, hideTotal);
  }

  return slices.flatMap(group => {
    const set = groupedPanels(withTotal, { ...scoped, records: group.records });
    if (!set?.panels.length) return [];
    return tabulate(specsOf(set.panels, sliceOf(group), true), overall.spine,
      overall.spineTotal, extra, scoped, table, hideTotal);
  });
}

/**
 * Everything a block may need beyond its own filtered aggregates. Grew past the point where
 * positional parameters read clearly -- `contextN` and `overallAggregates` were already optional
 * trailing arguments and `records` made three.
 */
export interface SeriesContext {
  variableMap?: Record<string, string>;
  layout?: SurveyLayout;
  /** The containing group's n, for compare mode's "Not asked" rebase. */
  contextN?: number;
  /** Aggregates over every respondent, ignoring the rail. Used by categorical-distribution. */
  overallAggregates?: AggregateTable;
  /** Rail-filtered respondent records, for the types that aggregate per group themselves. */
  records?: RespondentRecord[];
  /** Each respondent's weight, when the deck is weighted. Must be the one `aggregates` was built with. */
  weightOf?: WeightOf;
  /** The deck's statistics settings, for the modules that test what they show. */
  statistics?: ReportStatistics;
}

/**
 * Modules that neither sort nor trim what they are given: a stacked distribution's answer scale, a
 * funnel's journey, a ranking's rank positions. Two reasons behind one rule -- for the first three
 * the given order *is* the finding, while a ranked driver orders its rows itself, on a figure the
 * generic sort cannot see. Trimming is wrong for both: it would drop rows that the module has
 * already decided the order of. Exported because the editor has to hide the two controls as well,
 * and `report-design-edit` has to refuse the two patches.
 */
export function keepsGivenOrder(reportType: ReportType): boolean {
  return reportType === 'stacked-scale' || reportType === 'funnel'
    || reportType === 'diverging-bars' || reportType === 'tiered-ranking'
    || reportType === 'scatter-plot' || reportType === 'ownership-matrix'
    || reportType === 'stage-matrix'
    // A third reason, on this one: its bands are counted off the ends of the answer list, so a sort
    // or a top-N would move answers between bands and change the score.
    || reportType === 'kpi-score';
}

/**
 * Whether this block groups its answer list into named bands, which makes its order load-bearing.
 *
 * Read off the settings rather than the type: the same module bands one question and ranks the
 * next, so this cannot be a list of report types the way `keepsGivenOrder` is.
 */
export function bandsItsScale(block: PageSection): boolean {
  // A stacked distribution scores by bands even without any set: an older design's box count is
  // turned into the bands it meant. Either way its scores are counted by position.
  if (block.reportType === 'stacked-scale') {
    const extra = block.reportExtraData;
    const ranked = !!extra.barOrder && extra.barOrder !== 'asked';
    if (extra.showNet || extra.showShare || ranked) return true;
  }
  return !!(block.reportExtraData as { bands?: unknown[] }).bands?.length;
}

/** The same rule as a type guard, so the union still narrows where the settings are read off. */
function ordersItsOwn(block: PageSection): block is Extract<PageSection, {
  reportType: 'stacked-scale' | 'funnel' | 'diverging-bars' | 'tiered-ranking'
    | 'scatter-plot' | 'ownership-matrix' | 'stage-matrix' | 'kpi-score';
}> {
  return keepsGivenOrder(block.reportType);
}

/**
 * One series for an open-end: no points, the answers instead.
 *
 * `known` false where the key resolves to nothing *or* to a question that is not an open-end -- the
 * host renders that as "No data for X", which is the right answer for a design pointing this module
 * at a rating scale, and better than a table of numbers-as-text.
 */
function verbatimSeries(key: string, context: SeriesContext, cap?: number): ChartSeries {
  const { variableMap = {}, layout = {}, records = [] } = context;
  // Through variableMap, exactly as every other block resolves: a key that would render "No data"
  // on a chart has to render it here too, or a broken reference looks fine in one module and not
  // in the next.
  const position = variableMap[key];
  const variable = position ? layout[position] : undefined;
  const name = variableLabel(key, variableMap, layout);

  if (!variable || variable.kind !== 'text') {
    return { name, points: [], base: 0, multiResponse: false, known: false };
  }

  const answered = records
    .map(record => textOf(record[variable.column]))
    .filter(answer => !!answer);

  return {
    name,
    points: [],
    base: answered.length,
    multiResponse: false,
    known: true,
    verbatims: cap === undefined ? answered : answered.slice(0, cap),
  };
}

/** One box a respondent drew on a heatmap's image, in that image's own pixels. */
export interface HeatmapBox {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** What a heatmap module draws for one click category. */
export interface HeatmapSelections {
  /** The question's data position, which is what its image is stored under. */
  question: string;
  /** Each respondent who drew at least one box in this category, as their boxes. */
  respondents: HeatmapBox[][];
  /** How many chose "none of these", where the question offers it. */
  optOut?: { label: string; count: number };
}

function cornerOf(value: unknown): { x: number; y: number } | null {
  if (!value || typeof value !== 'object') return null;
  const { x, y } = value as { x?: unknown; y?: unknown };
  return typeof x === 'number' && typeof y === 'number' && Number.isFinite(x) && Number.isFinite(y)
    ? { x, y }
    : null;
}

function parsedJson(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * The boxes in one heatmap cell. Decipher stores them as JSON text --
 * `[[{"segment":0,"from":{"x":440,"y":64},"to":{"x":598,"y":142}}]]` -- and the outer nesting is not
 * documented, so every array is walked and anything with two corners is a box. The corners come in
 * whichever order the box was dragged. A cell some path has already parsed into an array is read
 * as it is.
 */
export function heatmapBoxesOf(value: AnswerValue | AnswerValue[] | undefined): HeatmapBox[] {
  const parsed: unknown = Array.isArray(value) ? value : parsedJson(textOf(value));
  const boxes: HeatmapBox[] = [];

  const walk = (node: unknown): void => {
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (!node || typeof node !== 'object') return;
    const { from, to } = node as { from?: unknown; to?: unknown };
    const a = cornerOf(from);
    const b = cornerOf(to);
    if (!a || !b) return;
    boxes.push({
      left: Math.min(a.x, b.x),
      top: Math.min(a.y, b.y),
      right: Math.max(a.x, b.x),
      bottom: Math.max(a.y, b.y),
    });
  };

  walk(parsed);
  return boxes;
}

/**
 * One series per click category of an image-highlighter question, carrying the boxes rather than
 * counts -- the shading depends on the image's size, which only the block knows once it has loaded.
 * Unknown on the same terms as `verbatimSeries`: a key that resolves to nothing, or to a question
 * that is not a heatmap.
 */
function heatmapSeries(key: string, context: SeriesContext): ChartSeries[] {
  const { variableMap = {}, layout = {}, records = [] } = context;
  const position = variableMap[key];
  const variable = position ? layout[position] : undefined;

  if (!variable || variable.kind !== 'heatmap') {
    const name = variableLabel(key, variableMap, layout);
    return [{ name, points: [], base: 0, multiResponse: false, known: false }];
  }

  const base = baseFor(records, variable);
  const optOutColumn = variable.optOut;
  const optOut = optOutColumn
    ? {
      label: optOutColumn.label,
      count: records.filter(record => isSelected(record[optOutColumn.column])).length,
    }
    : undefined;

  return variable.columns.map(column => ({
    name: column.label,
    points: [],
    base,
    multiResponse: false,
    known: true,
    heatmap: {
      question: position,
      respondents: records
        .map(record => heatmapBoxesOf(record[column.column]))
        .filter(boxes => boxes.length > 0),
      optOut,
    },
  }));
}

export function seriesForBlock(
  block: PageSection,
  aggregates: AggregateTable,
  context: SeriesContext = {},
): ChartSeries[] {
  const { variableMap, layout, contextN, overallAggregates } = context;

  // Before the destructure below, which needs every remaining member to declare these two --
  // returning here narrows methodology out rather than forcing it to carry fields it has no use for.
  if (block.reportType === 'study-overview' || block.reportType === 'headline-stats'
    || block.reportType === 'action-list'
    || block.reportType === 'stage-explainer'
    || block.reportType === 'text' || block.reportType === 'image') {
    return [];
  }

  // A dial with no question is one of those; a dial with one wants the ordinary series, so it falls
  // through to the generic tail below rather than getting an arm of its own.
  if (block.reportType === 'kpi-score' && !block.reportExtraData.variableKey) {
    return [];
  }

  // The answers themselves rather than a count of them. Before the destructure below for the same
  // reason methodology is: this module declares neither sorting nor a top-N, because there is no
  // answer list to order or trim.
  if (block.reportType === 'open-end-explorer' || block.reportType === 'word-cloud') {
    // The list is capped and says so; the cloud counts every answer. See MAX_VERBATIMS.
    const cap = block.reportType === 'open-end-explorer' ? MAX_VERBATIMS : undefined;
    return [verbatimSeries(block.reportExtraData.variableKey, context, cap)];
  }

  // The boxes respondents drew, one series per click category. Before the destructure for the same
  // reason: there is no answer list to sort or trim.
  if (block.reportType === 'heatmap') {
    return heatmapSeries(block.reportExtraData.variableKey, context);
  }

  // Two modules have neither, deliberately: a stacked distribution's scale and a funnel's journey
  // are both orders that sorting or trimming would destroy, which is the one thing each exists to
  // show. Narrowed out here rather than declaring two fields they would only ignore.
  //
  // A block that bands its scale joins them for the same reason, but by its settings rather than by
  // its type: bands are counted off the ends of the answer list, so sorting would move answers
  // between them and a top-N would drop answers a band still expects. `fieldAppliesTo` cannot say
  // this -- it is answered per type and cannot see that this block has bands -- so the editor hides
  // the two controls and this drops whatever a block was carrying before they were hidden.
  const { sortDescending, topN } = ordersItsOwn(block) || bandsItsScale(block)
    ? { sortDescending: undefined, topN: undefined }
    : block.reportExtraData;

  if (block.reportType === 'subgroup-panels') {
    return groupedSeries(block.reportExtraData, context);
  }

  // A crosstab is the same aggregation as a grid of panels, read the other way up: one series per
  // column, every series aligned to one spine of rows. Deliberately not its own arithmetic -- the
  // figures in a table and the figures in the chart beside it must be the same figures, and the
  // surest way to guarantee that is for there to be only one implementation of them.
  if (block.reportType === 'crosstab') {
    return crosstabSeries(block.reportExtraData, context);
  }

  // One series per market, each carrying the metric's answers. Ordering and trimming are the
  // block's own job there: they apply to the tiles, not to the answers inside one.
  if (block.reportType === 'segment-tiles') {
    const { variableKey, groupBy, subgroups, includeTotal } = block.reportExtraData;
    return groupedSeries({ variableKey, groupBy, subgroups, includeTotal }, context);
  }

  if (block.reportType === 'filter-comparison') {
    const key = block.reportExtraData.variableKey;
    const filtered = aggregates[key] ?? EMPTY_AGGREGATE;
    // Without an overall table both series come from the filtered one, which renders the pair flush
    // rather than failing -- the same thing it does when no filter is active.
    const overall = overallAggregates?.[key] ?? filtered;
    return distributionSeries(key, overall, filtered, sortDescending, topN, variableMap, layout);
  }

  // A funnel resolves its stages exactly as a bar chart resolves its bars -- the options of one
  // question collapse into a single series, several questions each keep their own -- and the block
  // reads one figure per stage out of whichever shape it gets.
  if (block.reportType === 'bar-chart' || block.reportType === 'funnel') {
    const combined = columnSeries(
      blockVariableKeys(block), aggregates, { sortDescending, topN }, context);
    if (combined) return [combined];
    return barSeries(block, aggregates, { sortDescending, topN }, context);
  }

  return blockVariableKeys(block).map(key => {
    const found = aggregates[key] ?? EMPTY_AGGREGATE;
    // Bands are counted off the answer list by position, so every answer has to be there -- including
    // one nobody in a filtered or compared subgroup picked, which `aggregate()` drops. Missing, the
    // list comes up short and the score blanks, or the bands slide onto the wrong answers and the
    // score is simply wrong. A dial is filled before it has bands too, so the first band is written
    // against the whole scale rather than the part of it this sample happened to pick.
    const aggregated = bandsItsScale(block) || block.reportType === 'kpi-score'
      ? { ...found, points: zeroFilled(found.points, key, variableMap, layout) }
      : found;

    // topN is ignored on a single-select pie or donut: the visible segments would fill the circle
    // while their labels summed to under 100%. It still applies to bars and to the donut grid,
    // where each mark is self-contained.
    const wholeCircle = block.reportType === 'pie-chart' || block.reportType === 'donut-chart';
    const limit = wholeCircle && !aggregated.multiResponse ? undefined : topN;
    // Not on a numeric battery: "Not asked" is the slice of a compare column that never saw the
    // question, expressed as a share of that column -- there is no share to take of a mean.
    const all = aggregated.numeric
      ? aggregated.points
      : withNotAsked(aggregated.points, aggregated.base, contextN, aggregated.sumWeights);
    const points = applySettings(all, sortDescending, limit);

    return {
      name: variableLabel(key, variableMap, layout),
      points,
      base: aggregated.base,
      multiResponse: aggregated.multiResponse,
      known: aggregated.known,
      numeric: aggregated.numeric,
      truncated: points.length < all.length ? { shown: points.length, total: all.length } : undefined,
    };
  });
}

export interface PageRow {
  full?: PageSection;
  left?: PageSection;
  right?: PageSection;
}

/**
 * Which pages a subsection has, in order, as the numbers its blocks carry.
 *
 * Derived rather than stored: a page exists because a block says it is on one. A subsection whose
 * blocks say nothing is a single page `[0]`, which is every design written before pages existed.
 * Gaps are not filled -- blocks on pages 0 and 5 are two pages, not six -- because a page with
 * nothing on it is not a page, it is a number nobody used.
 */
export function pagesOf(subSection: ReportSubSection): number[] {
  const seen = new Set<number>();
  for (const id of subSection.pageSectionOrder) {
    const block = subSection.pageSections[id];
    if (block) seen.add(pageOf(block));
  }
  return seen.size ? [...seen].sort((a, b) => a - b) : [0];
}

/** A block's page. Absent, negative and non-integer all mean the first. */
export function pageOf(block: PageSection): number {
  const page = block.page;
  return typeof page === 'number' && Number.isFinite(page) && page > 0 ? Math.trunc(page) : 0;
}

/**
 * The blocks of one page, laid out. `page` omitted lays out the whole subsection, which is what
 * anything reading a design as a flat list of blocks wants -- the published bundle's variable scan,
 * the compare-by grid -- and what a single-page subsection produces anyway.
 */
export function layoutRows(subSection: ReportSubSection, page?: number): PageRow[] {
  const rows: PageRow[] = [];
  let pair: PageRow | null = null;

  for (const id of subSection.pageSectionOrder) {
    const block = subSection.pageSections[id];
    if (!block) continue;
    if (page !== undefined && pageOf(block) !== page) continue;

    if (block.segmentLocation === 'full') {
      rows.push({ full: block });
      pair = null;
      continue;
    }
    if (!pair || pair[block.segmentLocation]) {
      pair = {};
      rows.push(pair);
    }
    pair[block.segmentLocation] = block;
  }

  return rows;
}

export interface ChartPoint {
  label: string;
  value: number;
  percent: number;
  optionIndex: number;
}

/** A layout entry standing for one column of a question, as parseDatamap's last pass builds them. */
type ColumnVariable = Extract<SurveyVariable, { kind: 'multi' | 'numeric' }> & { partOf: string };

/**
 * One series for a bar chart whose keys each name a single column -- nine options of a multi-punch
 * question, nine items of a MaxDiff battery, or a mix drawn from several questions. Each of those
 * yields exactly one number, so left as they are they draw nine one-bar charts behind a legend of
 * nine full question labels. Combined, they are the chart that was meant.
 *
 * Null unless every key resolves to a column, which is what keeps this off every chart that renders
 * today: a block naming whole questions takes the ordinary path below, unchanged.
 */
function columnSeries(
  keys: string[],
  aggregates: AggregateTable,
  settings: { sortDescending?: boolean; topN?: number },
  context: SeriesContext,
): ChartSeries | null {
  const { variableMap = {}, layout = {}, contextN } = context;
  if (keys.length < 2) return null;

  const columns = keys
    .map(key => variableOf(key, variableMap, layout))
    .filter((variable): variable is ColumnVariable =>
      !!variable && variable.kind !== 'coded' && !!variable.partOf);
  if (columns.length !== keys.length) return null;

  // A mean and a percentage cannot share one axis.
  const kinds = new Set(columns.map(variable => variable.kind));
  if (kinds.size !== 1) return null;

  const numeric = kinds.has('numeric');
  // Labelled from the layout rather than the data, so a compare column where nobody picked an
  // option still contributes its bar in the same position instead of shortening the chart.
  const points: ChartPoint[] = keys.map((key, optionIndex) => {
    const point = aggregates[key]?.points[0];
    return {
      label: variableLabel(key, variableMap, layout),
      value: point?.value ?? 0,
      percent: point?.percent ?? 0,
      optionIndex,
    };
  });

  const base = Math.max(0, ...keys.map(key => aggregates[key]?.base ?? 0));
  // Once, on the combined set -- per key it would add one "Not asked" bar per item, and sorting a
  // single point does nothing, so `sortDescending` would silently have no effect.
  const exactBase = Math.max(0, ...keys.map(key => aggregates[key]?.sumWeights ?? aggregates[key]?.base ?? 0));
  const all = numeric ? points : withNotAsked(points, base, contextN, exactBase);
  const ordered = applySettings(all, settings.sortDescending, settings.topN);

  const parents = [...new Set(
    columns.map(variable => layout[variable.partOf]?.label).filter(Boolean))];

  return {
    name: parents.join(', '),
    points: ordered,
    base,
    multiResponse: !numeric,
    known: true,
    numeric: numeric || undefined,
    truncated: ordered.length < all.length
      ? { shown: ordered.length, total: all.length }
      : undefined,
  };
}

/**
 * Every label any of these questions has, in the first question's order and then in the order the
 * rest introduce them. Deliberately unsorted: an unsorted chart has to keep the datamap's option
 * order, which the first question's points already carry.
 */
function unionSpine(pointSets: readonly ChartPoint[][]): ChartPoint[] {
  const spine: ChartPoint[] = [];
  const seen = new Set<string>();

  for (const points of pointSets) {
    for (const point of points) {
      if (seen.has(point.label)) continue;
      seen.add(point.label);
      spine.push(point);
    }
  }

  return spine;
}

/**
 * One series per charted question, all on one category axis.
 *
 * The axis is built from a spine every question is then aligned to, because the block draws its
 * categories from one series and hands the rest to Highcharts *positionally*. Sorting and top-N-ing
 * each question independently -- which is what this did -- puts a different answer at the same axis
 * position in each series, so a chart of several questions could be silently mislabelled. It bit
 * whenever `sortDescending` was on, whenever `topN` cut a different set per question, and whenever
 * the questions had different option lists.
 */
function barSeries(
  block: PageSection,
  aggregates: AggregateTable,
  settings: { sortDescending?: boolean; topN?: number },
  context: SeriesContext,
): ChartSeries[] {
  const { variableMap = {}, layout = {}, contextN } = context;
  const keys = blockVariableKeys(block);
  // One question drawn in asked order keeps every answer on the axis, the unpicked ones at zero:
  // it is the chart bands are authored against, and they are counted by position, the same as on
  // a dial. Missing, an answer nobody picked cannot be put in a band, and a band written while it
  // was missing slides onto the wrong answers the moment someone picks it. Not only once bands
  // exist, for that second reason. A sorted or trimmed chart is a ranking, where a zero is noise.
  const wholeScale = block.reportType === 'bar-chart' && keys.length === 1
    && (bandsItsScale(block) || (!settings.sortDescending && !settings.topN));

  const entries = keys.map(key => {
    const found = aggregates[key] ?? EMPTY_AGGREGATE;
    const aggregated = wholeScale
      ? { ...found, points: zeroFilled(found.points, key, variableMap, layout) }
      : found;
    return {
      key,
      aggregated,
      points: aggregated.numeric
        ? aggregated.points
        : withNotAsked(aggregated.points, aggregated.base, contextN, aggregated.sumWeights),
    };
  });

  const known = entries.filter(entry => entry.aggregated.known);
  const all = unionSpine(known.map(entry => entry.points));

  // Ranked on the highest share any question gives an option, not on its count: the questions can
  // have different bases, and ordering by count would rank the axis by which question more people
  // answered rather than by which answer was picked most.
  const peak = new Map<string, number>();
  for (const entry of known) {
    for (const point of entry.points) {
      peak.set(point.label, Math.max(peak.get(point.label) ?? 0, point.percent));
    }
  }

  const spine = applySettings(all, settings.sortDescending, settings.topN,
    point => peak.get(point.label) ?? 0);
  // Reported once, on every series, so the host's "top N of M options" caption reads the same
  // figure whichever series it happens to look at.
  const truncated = spine.length < all.length
    ? { shown: spine.length, total: all.length }
    : undefined;

  return entries.map(entry => ({
    name: variableLabel(entry.key, variableMap, layout),
    // An unresolved question keeps its series, empty, so the host can still name it as missing.
    points: entry.aggregated.known ? alignedTo(spine, entry.points) : [],
    base: entry.aggregated.base,
    multiResponse: entry.aggregated.multiResponse,
    known: entry.aggregated.known,
    numeric: entry.aggregated.numeric,
    truncated: entry.aggregated.known ? truncated : undefined,
  }));
}

/**
 * How many open-ends a module that *lists* them will hold.
 *
 * A guard on the browser rather than a feature: the deck already has every respondent's answer in
 * memory, so this does not save a fetch -- it stops one module from rendering a table of ten
 * thousand paragraphs, which is a subsection nobody reads and a scroll nobody finishes. The block says
 * how many it is showing out of how many answered.
 *
 * A module that *counts* words rather than listing answers is not capped: it draws a fixed number
 * of words whatever the sample, so taking the first two thousand answers would quietly make every
 * word size a slice of the study rather than the study. Counting ten thousand answers is around a
 * tenth of a second, which is the cheaper of the two prices.
 */
export const MAX_VERBATIMS = 2000;

export interface ChartSeries {
  name: string;
  points: ChartPoint[];
  base: number;
  multiResponse: boolean;
  truncated?: { shown: number; total: number };
  known: boolean;
  /** Points are means, not percentages. See VariableAggregate.numeric. */
  numeric?: boolean;
  /**
   * The answers themselves, for the one module that shows text rather than counting it.
   *
   * Carried on the series rather than handed to the block as records, so the rule the whole
   * renderer runs on holds for open-ends too: a block draws what `seriesForBlock` gives it and
   * never reaches into respondent data itself. `base` is how many answered; this is how many of
   * those are here, which differs only when the cap bites.
   */
  verbatims?: string[];
  /** The boxes behind a heatmap, carried for the same reason `verbatims` is. */
  heatmap?: HeatmapSelections;
  /** A crosstab column's letter, true bases and test results. */
  crosstab?: CrosstabColumn;
}

export interface ReportBlock {
  block: PageSection;
  series: ChartSeries[];
  palette: ColorPalette;
  alignCount?: number;
  compact?: boolean;
  /** How the study was run. Only the methodology module reads it. */
  studyFacts?: StudyFacts;
  /** Set while the deck is in edit mode, for modules whose content is edited in place. */
  editing?: boolean;
  /**
   * How such a module reports a change. A callback rather than an @Output because blocks are
   * created by *ngComponentOutlet, which binds inputs but has no way to bind outputs.
   */
  onPatch?: (patch: BlockPatch) => void;
  /** Crosstab only: how a reader drills into it. Absent where it cannot be explored. */
  explore?: ExploreHandle;
}

interface DatamapVariable {
  vgroup: string;
  label: string;
  /** The question this variable belongs to, which is how a grid's rows find their heading. */
  qlabel?: string;
  qtitle: string | null;
  title: string | null;
  rowTitle: string | null;
  /** A grid cell's column, which is what distinguishes it from its siblings. */
  colTitle?: string | null;
  type: string;
  values?: { label: string; value: number; title: string }[] | null;
}

interface DatamapQuestion {
  qlabel: string;
  qtitle: string | null;
  type: string;
  /** Decipher's question flags: `v` virtual, `x` execute-autofilled, `t` transient/system. */
  flags?: string | null;
  /** The dynamic question it was built with -- `autosum`, `cardrating`, `imadvanced` -- if any. */
  dq?: string | null;
  variables: DatamapVariable[];
}

export interface DecipherDatamap {
  variables: DatamapVariable[];
  questions: DatamapQuestion[];
}

function displayText(variable: DatamapVariable): string {
  return variable.rowTitle || variable.title || variable.qtitle || variable.label;
}

/**
 * What one column of a multi-punch question is called.
 *
 * `colTitle` first, which matters only for a grid and matters completely there. In a grid every cell
 * of a row carries the *same* `rowTitle` -- it names the row, which is the question itself -- while
 * `colTitle` is what tells one cell from another. Reading the row title would label every column of
 * an association grid identically and collapse the whole matrix into one column.
 *
 * Falls through to displayText everywhere else, so a flat multi-punch question, whose variables
 * carry a rowTitle each and no colTitle, is unaffected.
 */
function columnText(variable: DatamapVariable): string {
  return variable.colTitle || displayText(variable);
}

/**
 * How many variables must share a battery's item pool before they read as its task screens rather
 * than as a question that happens to reuse the same answer list. One exercise contributes two per
 * screen; a question reusing a brand list contributes one, and must never be hidden by accident.
 */
const MIN_TASK_COLUMNS = 4;

/**
 * The task screens of a MaxDiff or conjoint exercise: coded variables whose answer list *is* the
 * item pool of a numeric battery in the same survey. "Which statement won on screen 3" depends on
 * which statements that respondent was shown, so it is not a question anyone charts -- and one
 * exercise contributes twenty of them, which is a sixth of a picker.
 *
 * Identified by shape rather than by Decipher's "flags", deliberately: `t` alone would also take
 * the sentiment and timing variables, which is why the datamap trim requires both `x` and `t`.
 */
function taskColumns(datamap: DecipherDatamap): Set<string> {
  const itemPools = (datamap.questions ?? [])
    .filter(question => question.type === 'float' || question.type === 'number')
    .filter(question => !(question.flags ?? '').includes('x'))
    .map(question => (question.variables ?? []).filter(variable => !!variable.label))
    .filter(items => items.length >= 2)
    .map(items => new Set(items.map(displayText)));

  const sharing = new Map<Set<string>, string[]>(itemPools.map(pool => [pool, []]));
  for (const variable of datamap.variables ?? []) {
    if (!variable.values?.length) continue;
    const answers = variable.values.map(value => value.title ?? value.label);
    for (const pool of itemPools) {
      if (answers.length !== pool.size) continue;
      if (answers.every(answer => pool.has(answer))) sharing.get(pool)!.push(variable.label);
    }
  }

  const tasks = new Set<string>();
  for (const labels of sharing.values()) {
    if (labels.length < MIN_TASK_COLUMNS) continue;
    for (const label of labels) tasks.add(label);
  }
  return tasks;
}

/** Decipher's dynamic questions whose answers are boxes drawn over an image. */
const HEATMAP_DQS: ReadonlySet<string> = new Set(['imadvanced']);

function isHeatmapQuestion(question: DatamapQuestion): boolean {
  return HEATMAP_DQS.has(question.dq ?? '');
}

/**
 * A question's "none of these" answer. Decipher lists every `<noanswer>` under one pseudo-question
 * labelled `noanswer`, as a 0/1 variable named `noanswer{qlabel}_{row}` whose row title is the
 * question's own text, a colon, and then the answer. The title check keeps `Q1` from claiming the
 * opt-out of a question labelled `Q1_b`.
 */
function optOutOf(
  datamap: DecipherDatamap,
  question: DatamapQuestion,
): { column: string; label: string } | undefined {
  const prefix = `noanswer${question.qlabel}_`;
  const asked = question.qtitle ? `${question.qtitle}: ` : '';
  const variable = (datamap.variables ?? []).find(candidate =>
    candidate.label?.startsWith(prefix) && (candidate.rowTitle ?? '').startsWith(asked));
  if (!variable) return undefined;

  const label = (variable.rowTitle ?? '').slice(asked.length).trim();
  return { column: variable.label, label: label || 'None of these' };
}

export function parseDatamap(datamap: DecipherDatamap): SurveyLayout {
  const layout: SurveyLayout = {};
  const tasks = taskColumns(datamap);

  // A grid asks the same thing about several items, and each row is its own coded variable -- so
  // the picker shows "Clean energy", "Renewable energy" with nothing saying what was asked about
  // them. Only for a question with more than one: a single-variable question is its own heading.
  const headings = new Map<string, string>();
  for (const question of datamap.questions ?? []) {
    if ((question.variables ?? []).length < 2) continue;
    // The code is in the heading because that is the half the rows themselves never carry: a row
    // reads "Clean energy", and "Q15" appears nowhere on it.
    headings.set(question.qlabel,
      question.qtitle ? `${question.qlabel} — ${question.qtitle}` : question.qlabel);
  }

  for (const variable of datamap.variables ?? []) {
    if (!variable.values?.length) continue;
    if (tasks.has(variable.label)) continue;
    const options: AnswerOption[] = variable.values.map(value => ({
      value: value.value,
      label: value.title ?? value.label,
    }));
    layout[variable.label] = {
      kind: 'coded',
      label: displayText(variable),
      column: variable.label,
      options,
      group: variable.qlabel ? headings.get(variable.qlabel) : undefined,
    };
  }

  for (const question of datamap.questions ?? []) {
    if (question.type !== 'multiple') continue;
    const columns = (question.variables ?? [])
      .filter(variable => !!variable.label)
      .map(variable => ({ column: variable.label, label: columnText(variable) }));
    if (!columns.length) continue;

    layout[question.qlabel] = {
      kind: 'multi',
      label: question.qtitle ?? question.qlabel,
      columns,
    };
  }

  // Numeric batteries: a float/number question whose variables each hold a score. These carry no
  // answer `values`, so the first loop skips every one of them -- which is why a MaxDiff block could
  // reference them and resolve to nothing at all.
  for (const question of datamap.questions ?? []) {
    if (question.type !== 'float' && question.type !== 'number') continue;
    // Flags live on the question, never on the variable. `x` is Decipher's execute-autofilled
    // marker, which is what the survey plumbing is -- dvscore, sample source, hidden age recodes.
    if ((question.flags ?? '').includes('x')) continue;

    const columns = (question.variables ?? [])
      .filter(variable => !!variable.label)
      .map(variable => ({ column: variable.label, label: displayText(variable) }));
    // A single number -- an interview length, a birth year -- is a fact about a respondent, not a
    // set of items to compare. Only a battery charts.
    if (columns.length < 2) continue;

    layout[question.qlabel] = {
      kind: 'numeric',
      label: question.qtitle ?? question.qlabel,
      columns,
    };
  }

  // Image-highlighter questions, keyed by the question so every click category comes with it.
  // Recognised by Decipher's dynamic-question name: their columns are text holding JSON boxes, and
  // nothing about a text column says so.
  for (const question of datamap.questions ?? []) {
    if (!isHeatmapQuestion(question)) continue;
    const columns = (question.variables ?? [])
      .filter(variable => !!variable.label)
      .map(variable => ({ column: variable.label, label: columnText(variable) }));
    if (!columns.length) continue;

    layout[question.qlabel] = {
      kind: 'heatmap',
      label: question.qtitle ?? question.qlabel,
      columns,
      optOut: optOutOf(datamap, question),
    };
  }

  // Open-ends. Flags decide which are real questions: `x` is Decipher's execute-autofilled marker,
  // which is the survey plumbing (exit status, vendor state, the GeoIP columns), and `t` is
  // transient or system-generated (the AI summary and sentiment columns beside an open-end, the
  // start timestamp). Both excluded, and what is left is what somebody actually typed an answer to.
  // Stricter than the numeric rule above, which only drops `x`, because a text question has no
  // shape to disqualify it -- every one of these columns holds a string.
  for (const question of datamap.questions ?? []) {
    if (question.type !== 'text') continue;
    const flags = question.flags ?? '';
    if (flags.includes('x') || flags.includes('t')) continue;
    // Read by the heatmap above. Offered here as well, its boxes would be listed as answers.
    if (isHeatmapQuestion(question)) continue;

    for (const variable of question.variables ?? []) {
      if (!variable.label || layout[variable.label]) continue;
      layout[variable.label] = {
        kind: 'text',
        label: displayText(variable),
        column: variable.label,
        group: headings.get(question.qlabel),
      };
    }
  }

  // One entry per column of the questions above, so a design can name a single option of a
  // multi-punch question or a single item of a battery -- "% who said reduced pollution" as one
  // measure, rather than only as one bar of its question's chart. `partOf` records which question
  // it came out of, which is what tells the renderer these belong on one axis together and keeps
  // them out of the places a single column makes no sense.
  //
  // Only where the name is free: a real question always wins, and for a one-variable question the
  // qlabel and the column name are the same string.
  // Not a heatmap's: one click category on its own is still a heatmap, not a measure.
  const columnsOf = (variable: SurveyVariable): { column: string; label: string }[] =>
    variable.kind === 'coded' || variable.kind === 'text' || variable.kind === 'heatmap'
      ? []
      : variable.columns;

  for (const [position, variable] of Object.entries({ ...layout })) {
    for (const column of columnsOf(variable)) {
      if (layout[column.column]) continue;
      layout[column.column] = {
        kind: variable.kind as 'multi' | 'numeric',
        label: column.label,
        columns: [column],
        partOf: position,
      };
    }
  }

  return layout;
}
